import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getAllUserOrders } from "../../services/order.service";
import { trackRealtime,  trackShipment } from "../../services/logistic.service";
import { reviewService } from "../../services/review.service";
import {
  MdHistory,
  MdExpandMore,
  MdExpandLess,
  MdArrowBack,
  MdShoppingBag,
  MdLocationOn,
  MdLocalShipping,
  MdStore,
  MdReceipt,
  MdSchedule,
  MdErrorOutline,
  MdRateReview,
  MdPerson,
  MdTrendingUp,
  MdAccessTime,
  MdPause,
  MdPlayArrow,
  MdDownload,
} from "react-icons/md";
import CancelOrderButton from "./CancelOrderButton";
import ReturnOrderButton from "./ReturnOrderButton";
import { getBookings, submitBookingReview } from "../../services/booking.service";
import { useTranslation } from "react-i18next";
import { storeSettingsService } from "../../services/storeSettings.service";
import { useSelector } from "react-redux";
import { FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import {adjustColor,ThemedCard,getStatusColor,getTrackingStatusColor,getTrackingStatusIcon,formatDate,formatDateTime,renderStars } from "../../helpers/csshelper.js"
import { isDarkTheme } from "./themeTemplates/ThemeLayouts";
import ReviewModal from "../../pages/online-store/storeSetting/reviewModelOrderhistory.jsx";
import { LogoLoading } from "../../components/common/utlity/utility.js";
import { getStorefrontPath } from "./storefrontPaths";

const formatDeliverySlot = (dateValue, timeValue) => {
  const date = String(dateValue || '').trim();
  const time = String(timeValue || '').trim();

  if (date && time) {
    return `${date} at ${time}`;
  }

  return date || time || '';
};

const getOrderItemKey = (item = {}, index = 0) => {
  const productId = item?.productId || item?._id || "product";
  const variantId =
    item?.variantId ||
    item?.variantSku ||
    item?.variantName ||
    item?.variantBatchNumber ||
    "default";

  return `${productId}::${variantId}::${index}`;
};

const getItemDeliverySnapshot = (order, item, index) => {
  const orderedQuantity = Math.max(0, Number(item?.cartQuantity || item?.quantity || 1));
  const progress = Array.isArray(order?.partialDelivery?.itemProgress)
    ? order.partialDelivery.itemProgress.find((entry) => entry?.itemKey === getOrderItemKey(item, index))
    : null;

  if (!progress) {
    return {
      orderedQuantity,
      deliveredQuantity:
        String(order?.orderStatus || "").toLowerCase() === "delivered" ? orderedQuantity : 0,
      pendingQuantity:
        String(order?.orderStatus || "").toLowerCase() === "delivered" ? 0 : orderedQuantity,
    };
  }

  return {
    orderedQuantity,
    deliveredQuantity: Math.max(0, Number(progress?.deliveredQuantity || 0)),
    pendingQuantity: Math.max(0, Number(progress?.pendingQuantity || 0)),
    lastDeliveredAt: progress?.lastDeliveredAt || null,
  };
};

const getDeliveryInvoices = (order) => {
  const rawInvoices = order?.partialDelivery?.invoices;
  const invoiceEntries = Array.isArray(rawInvoices)
    ? rawInvoices
    : rawInvoices && typeof rawInvoices === "object"
      ? Object.values(rawInvoices)
      : [];

  const batchEntries = Array.isArray(order?.partialDelivery?.batches)
    ? order.partialDelivery.batches
    : [];

  const invoicesFromBatches = batchEntries
    .filter((batch) => batch?.invoiceId)
    .map((batch) => ({
      batchId: batch?.batchId || "",
      invoiceId: batch?.invoiceId || "",
      saleId: batch?.saleId || "",
      amount: Number(batch?.invoiceAmount || 0),
      subtotalAmount: Number(batch?.subtotalAmount || 0),
      taxAmount: Number(batch?.taxAmount || 0),
      shippingAmount: Number(batch?.shippingAmount || 0),
      status: batch?.status || "delivered",
      createdAt: batch?.deliveredAt || batch?.assignedAt || "",
      items: Array.isArray(batch?.items)
        ? batch.items.map((item) => ({
            ...item,
            quantity: Number(item?.quantity || 0),
            salePrice: Number(item?.salePrice || item?.price || 0),
            total: Number(
              (
                Number(item?.total || 0) ||
                Number(item?.salePrice || item?.price || 0) * Number(item?.quantity || 0)
              ).toFixed(2),
            ),
          }))
        : [],
    }));

  const mergedInvoices = [...invoiceEntries, ...invoicesFromBatches];
  if (!mergedInvoices.length) {
    return [];
  }

  const seen = new Set();
  const uniqueInvoices = mergedInvoices.filter((invoice) => {
    const key =
      String(invoice?.invoiceId || "") ||
      String(invoice?.saleId || "") ||
      String(invoice?.batchId || "");

    if (!key) {
      return false;
    }

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });

  return uniqueInvoices
    .sort((left, right) => {
      const leftTime = new Date(left?.createdAt || 0).getTime();
      const rightTime = new Date(right?.createdAt || 0).getTime();
      return rightTime - leftTime;
    })
    .map((invoice, index) => ({
      ...invoice,
      sequenceLabel: `Invoice ${index + 1}`,
      items: Array.isArray(invoice?.items) ? invoice.items : [],
      amount: Number(invoice?.amount || 0),
      subtotalAmount: Number(invoice?.subtotalAmount || 0),
      taxAmount: Number(invoice?.taxAmount || 0),
      shippingAmount: Number(invoice?.shippingAmount || 0),
    }));
};

const formatInvoiceDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleString();
};

const buildInvoiceDownloadLink = ({ order, invoice, businessName, bussinessUuid }) => {
  const invoicePath = getStorefrontPath({
    businessName,
    bussinessUuid,
    suffix: `invoice/${order?.orderId || ""}`,
  });

  const params = new URLSearchParams({
    download: "pdf",
    closeAfterDownload: "1",
    invoiceId: String(invoice?.invoiceId || ""),
    batchId: String(invoice?.batchId || ""),
  });

  return `${invoicePath}?${params.toString()}`;
};

const handleDownloadInvoicePdf = ({ order, invoice, businessName, bussinessUuid }) => {
  const downloadUrl = buildInvoiceDownloadLink({
    order,
    invoice,
    businessName,
    bussinessUuid,
  });
  window.open(downloadUrl, "_blank", "noopener,noreferrer");
};

// Theme hook
const useTheme = (businessUuid) => {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        setLoading(true);
        if (businessUuid) {
          try {
            const settingsResponse = await storeSettingsService.getPublicStoreSettings(businessUuid);

            if (settingsResponse.success && settingsResponse.data?.theme) {
              const themeData = settingsResponse.data.theme;
              setTheme(themeData);
              localStorage.setItem(`store_theme_${businessUuid}`, JSON.stringify(themeData));
              setLoading(false);
              return;
            }
          } catch (settingsError) {
          }
        }

        const savedTheme = localStorage.getItem(`store_theme_${businessUuid}`);
        if (savedTheme) {
          try {
            const parsedTheme = JSON.parse(savedTheme);
            setTheme(parsedTheme);
          } catch (e) {
            console.error("Error parsing saved theme:", e);
          }
        } else {
          setTheme({
            themeName: 'Modern Indigo',
            fontFamily: 'Inter',
            primaryColor: '#4f46e5',
            secondaryColor: '#10b981',
            backgroundColor: '#f9fafb',
            buttonColor: '#4f46e5',
            textColor: '#1f2937',
            headingColor: '#111827',
            borderRadius: '12px',
            buttonStyle: 'rounded',
            cardStyle: 'shadow',
            shadowColor: 'rgba(0,0,0,0.1)'
          });
        }
      } catch (error) {
        setTheme({
          themeName: 'Modern Indigo',
          fontFamily: 'Inter',
          primaryColor: '#4f46e5',
          secondaryColor: '#10b981',
          backgroundColor: '#f9fafb',
          buttonColor: '#4f46e5',
          textColor: '#1f2937',
          headingColor: '#111827',
          borderRadius: '12px',
          buttonStyle: 'rounded',
          cardStyle: 'shadow',
          shadowColor: 'rgba(0,0,0,0.1)'
        });
      } finally {
        setLoading(false);
      }
    };

    if (businessUuid) {
      loadTheme();
    }
  }, [businessUuid]);

  return { theme, loading };
};

// RESPONSIVE CustomerProfileBanner
function CustomerProfileBanner({ customerProfile, theme, onLogin }) {
  const { t } = useTranslation();
  const isDark = isDarkTheme(theme);

  if (!customerProfile) {
    return (
      <div className={isDark ? "border-b border-yellow-800" : "bg-yellow-50 border-b border-yellow-200"}
           style={{ backgroundColor: isDark ? adjustColor(theme?.backgroundColor || '#1f2937', 10) : undefined }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <MdPerson className="text-yellow-600 text-lg sm:text-xl flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800 text-xs sm:text-sm">{t("STORENEWDETAILS.ORDER_HISTORY.NOT_LOGGED_IN")}</p>
                <p className="text-[10px] sm:text-xs text-yellow-700">{t("STORENEWDETAILS.ORDER_HISTORY.LOGIN_MESSAGE")}</p>
              </div>
            </div>
            <button
              onClick={onLogin}
              className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium hover:opacity-90 transition-all shadow-sm text-xs sm:text-sm"
              style={{
                backgroundColor: theme?.buttonColor || '#4f46e5',
                color: '#ffffff',
                borderRadius: theme?.borderRadius || '8px'
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.LOGIN")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isDark ? "border-b" : "bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200"}
         style={{
           backgroundColor: isDark ? adjustColor(theme?.backgroundColor || '#1f2937', 10) : undefined,
           borderColor: isDark ? '#374151' : undefined
         }}>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white flex items-center justify-center shadow-sm flex-shrink-0"
                 style={{
                   color: theme?.primaryColor || '#4f46e5',
                   borderRadius: theme?.borderRadius || '50%'
                 }}>
              <span className="font-bold text-sm sm:text-base">
                {customerProfile.name ? customerProfile.name.charAt(0).toUpperCase() : 'C'}
              </span>
            </div>
            <div>
              <p className="font-medium text-xs sm:text-sm" style={{ color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827') }}>
                {customerProfile.name || customerProfile.customerName || 'Customer'}
              </p>
              <p className="text-[10px] sm:text-sm" style={{ color: theme?.textColor + '80' || '#6b7280' }}>
                {customerProfile.customer_mobile || customerProfile.phone || t("STORENEWDETAILS.ORDER_HISTORY.NO_PHONE")}
              </p>
            </div>
          </div>
          {customerProfile.isGuest && (
            <span className="text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
              {t("STORENEWDETAILS.ORDER_HISTORY.GUEST_ACCOUNT")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// RESPONSIVE Featured Products Card
function FeaturedProductsCard({ frequentProducts = [], recentProduct, theme, onProductClick }) {
  const { t } = useTranslation();
  const [frequentIndex, setFrequentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    let interval;
    if (isAutoPlaying && frequentProducts.length > 1) {
      interval = setInterval(() => {
        setFrequentIndex((prev) => (prev + 1) % frequentProducts.length);
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoPlaying, frequentProducts.length]);

  if (frequentProducts.length === 0 && !recentProduct) return null;

  const frequentGradient = `linear-gradient(135deg, ${theme?.primaryColor || '#4f46e5'}15, ${theme?.primaryColor || '#4f46e5'}05)`;
  const recentGradient = `linear-gradient(135deg, ${theme?.secondaryColor || '#10b981'}15, ${theme?.secondaryColor || '#10b981'}05)`;

  const nextFrequent = () => {
    setFrequentIndex((prev) => (prev + 1) % frequentProducts.length);
  };

  const prevFrequent = () => {
    setFrequentIndex((prev) => (prev - 1 + frequentProducts.length) % frequentProducts.length);
  };

  const goToFrequent = (index) => {
    setFrequentIndex(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <div className="mb-6 sm:mb-8">
      <div
        className="rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl"
        style={{
          background: isDarkTheme(theme) ? adjustColor(theme?.backgroundColor || '#1f2937', 15) : '#ffffff',
          border: `1px solid ${theme?.primaryColor || '#4f46e5'}20`,
          boxShadow: `0 10px 25px -5px ${theme?.primaryColor || '#4f46e5'}30`
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x"
             style={{ borderColor: `${theme?.primaryColor || '#edecf4'}20` }}>

          {/* LEFT COLUMN - Frequently Purchased */}
          <div className="p-3 sm:p-5">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{
                  background: `linear-gradient(135deg, ${theme?.primaryColor || '#4f46e5'}, ${theme?.primaryColor || '#4f46e5'}80)`,
                  boxShadow: `0 4px 10px ${theme?.primaryColor || '#4f46e5'}50`
                }}>
                  <MdTrendingUp className="text-white text-sm sm:text-lg" />
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base" style={{ color: theme?.primaryColor || '#4f46e5' }}>
                    {t("PRODUCT_PAGE.MOSTFREQUENTLY")}
                  </h3>
                  <p className="text-[10px] sm:text-xs opacity-60" style={{ color: theme?.textColor || '#4b5563' }}>
                    Your top picks
                  </p>
                </div>
              </div>

              {frequentProducts.length > 1 && (
                <div className="flex items-center gap-0.5 sm:gap-1">
                  <button
                    onClick={toggleAutoPlay}
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors mr-0.5 sm:mr-1"
                    style={{ color: theme?.primaryColor || '#4f46e5' }}
                    title={isAutoPlaying ? "Pause auto-scroll" : "Start auto-scroll"}
                  >
                    {isAutoPlaying ? <MdPause size={14} /> : <MdPlayArrow size={14} />}
                  </button>
                  <button
                    onClick={prevFrequent}
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-sm"
                    style={{ color: theme?.primaryColor || '#4f46e5' }}
                  >
                    ←
                  </button>
                  <button
                    onClick={nextFrequent}
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-sm"
                    style={{ color: theme?.primaryColor || '#4f46e5' }}
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            <div className="relative">
              {frequentProducts.length > 0 ? (
                <>
                  <div
                    key={frequentIndex}
                    className="animate-fadeIn"
                  >
                    <FeaturedProductCard
                      product={frequentProducts[frequentIndex]}
                      theme={theme}
                      type="frequent"
                      onClick={onProductClick}
                      index={frequentIndex + 1}
                      total={frequentProducts.length}
                    />
                  </div>

                  {isAutoPlaying && frequentProducts.length > 1 && (
                    <div className="absolute top-2 right-2">
                      <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full animate-progress"
                          style={{
                            backgroundColor: theme?.primaryColor || '#9382eb',
                            animation: 'progress 3s linear infinite'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {frequentProducts.length > 1 && (
                    <div className="flex justify-center gap-1 mt-2 sm:mt-3">
                      {frequentProducts.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            goToFrequent(idx);
                            setIsAutoPlaying(false);
                          }}
                          className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: idx === frequentIndex
                              ? '#dfdfe3'
                              : `${'#f3f2f8'}30`,
                            transform: idx === frequentIndex ? 'scale(1.2)' : 'scale(1)'
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <EmptyStateCard theme={theme} type="frequent" />
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - Recently Purchased */}
          <div className="p-3 sm:p-5">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              <div className="p-1.5 sm:p-2 rounded-lg flex-shrink-0" style={{
                background: `linear-gradient(135deg, ${theme?.secondaryColor || '#10b981'}, ${theme?.secondaryColor || '#10b981'}80)`,
                boxShadow: `0 4px 10px ${theme?.secondaryColor || '#10b981'}50`
              }}>
                <MdAccessTime className="text-white text-sm sm:text-lg" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base" style={{ color: theme?.secondaryColor || '#10b981' }}>
                  {t("PRODUCT_PAGE.RECENT")}
                </h3>
                <p className="text-[10px] sm:text-xs opacity-60" style={{ color: theme?.textColor || '#4b5563' }}>
                  Your latest purchase
                </p>
              </div>
              {recentProduct && (
                <div className="ml-auto bg-gradient-to-r from-blue-400 to-blue-500 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-lg">
                  New!
                </div>
              )}
            </div>

            {recentProduct ? (
              <FeaturedProductCard
                product={recentProduct}
                theme={theme}
                type="recent"
                onClick={onProductClick}
              />
            ) : (
              <EmptyStateCard theme={theme} type="recent" />
            )}
          </div>
        </div>

        <div
          className="px-4 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs text-center border-t"
          style={{
            borderColor: `${theme?.primaryColor || '#4f46e5'}20`,
            color: theme?.textColor + '80' || '#6b7280'
          }}
        >
          Click on any product to view details
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0.5; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-progress {
          animation: progress 3s linear infinite;
        }
      `}</style>
    </div>
  );
}

function FeaturedProductCard({ product, theme, type, onClick, index, total }) {
  const { t } = useTranslation();

  if (!product) return null;
  const getProductPrice = () => {
    if (product.salePrice) return product.salePrice;
    if (product.price) return product.price;
    if (product.variantSalePrice) return product.variantSalePrice;
    return 0;
  };

  const getProductName = () => {
    if (product.productName) return product.productName;
    if (product.name) return product.name;
    return "Product";
  };

  const getVariantDisplay = () => {
    if (product.variantName) return product.variantName;
    if (product.variantAttributes && product.variantAttributes.length > 0) {
      return product.variantAttributes.map(attr => attr.value).join(' - ');
    }
    return null;
  };

  const getImageUrl = () => {
    const image = product.image || product.itemPicture || product.productImage;
    if (!image) return null;
    return image.includes('http') ? image : `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${image}`;
  };

  const price = getProductPrice();
  const productName = getProductName();
  const variantDisplay = getVariantDisplay();
  const imageUrl = getImageUrl();

  const primaryColor = type === 'frequent'
    ? (theme?.primaryColor || '#4f46e5')
    : (theme?.secondaryColor || '#10b981');

  const lightColor = type === 'frequent'
    ? (theme?.primaryColor + '15' || '#4f46e515')
    : (theme?.secondaryColor + '15' || '#10b98115');

  return (
    <div
      onClick={() => onClick(product)}
      className="cursor-pointer group w-full"
    >
      <div className="overflow-hidden rounded-lg sm:rounded-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
        style={{
          background: isDarkTheme(theme) ? adjustColor(theme?.backgroundColor || '#1f2937', 15) : `linear-gradient(145deg, #ffffff, ${lightColor})`,
          border: `1px solid ${primaryColor}30`,
        }}
      >
        <div className="p-2.5 sm:p-4">
          <div className="flex items-center gap-2.5 sm:gap-4">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg sm:rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 relative"
              style={{
                background: `radial-gradient(circle at top left, ${primaryColor}, ${primaryColor})`,
                boxShadow: `0 8px 15px -5px ${primaryColor}`
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={productName}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              ) : (
                <MdShoppingBag className="text-white text-xl sm:text-3xl" />
              )}
              <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h3
                  className="font-bold text-sm sm:text-base group-hover:text-blue-600 transition-colors truncate"
                  style={{ color: theme?.headingColor || (isDarkTheme(theme) ? '#f9fafb' : '#111827') }}
                >
                  {productName}
                </h3>

                {variantDisplay && (
                  <span
                    className="inline-block text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium"
                    style={{
                      background: `linear-gradient(90deg, ${primaryColor}20, ${primaryColor}05)`,
                      color: primaryColor,
                      border: `1px solid ${primaryColor}40`
                    }}
                  >
                    {variantDisplay}
                  </span>
                )}
              </div>

              <div className="mt-1.5 sm:mt-2 flex items-center justify-between">
                <div className="flex items-baseline gap-0.5 sm:gap-1">
                  <span className="text-[10px] sm:text-xs opacity-60">₹</span>
                  <span className="font-bold text-lg sm:text-xl" style={{
                    color: primaryColor,
                    textShadow: `0 2px 5px ${primaryColor}40`
                  }}>
                    {price || '0.00'}
                  </span>
                </div>

                {type === 'frequent' && product.count && (
                  <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-bold" style={{
                    background: `linear-gradient(135deg, #fbbf24, #f59e0b)`,
                    color: '#78350f',
                    boxShadow: '0 2px 5px #f59e0b70'
                  }}>
                    🔥 {product.count}x
                  </span>
                )}

                {index && total && total > 1 && (
                  <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full" style={{
                    background: `${primaryColor}20`,
                    color: primaryColor
                  }}>
                    {index}/{total}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                {type === 'recent' && product.purchaseDate && (
                  <>
                    <MdAccessTime size={10} className="sm:text-xs" style={{ color: primaryColor }} />
                    <p className="text-[10px] sm:text-xs" style={{ color: theme?.textColor + '99' || '#6b7280' }}>
                      {formatDate(product.purchaseDate)}
                    </p>
                  </>
                )}
                {type === 'frequent' && product.lastPurchased && (
                  <>
                    <MdAccessTime size={10} className="sm:text-xs" style={{ color: primaryColor }} />
                    <p className="text-[10px] sm:text-xs" style={{ color: theme?.textColor + '99' || '#6b7280' }}>
                      Last: {formatDate(product.lastPurchased)}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="h-0.5 sm:h-1 w-full" style={{
          background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}80, transparent)`
        }} />
      </div>
    </div>
  );
}

function EmptyStateCard({ theme, type }) {
  const primaryColor = type === 'frequent'
    ? (theme?.primaryColor || '#4f46e5')
    : (theme?.secondaryColor || '#10b981');

  return (
    <div className="w-full cursor-pointer group">
      <div className="overflow-hidden rounded-lg sm:rounded-xl border-2 border-dashed p-3 sm:p-4 text-center"
        style={{ borderColor: `${primaryColor}40` }}
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${primaryColor}10` }}
          >
            {type === 'frequent' ? (
              <MdTrendingUp style={{ color: `${primaryColor}60` }} size={24} className="sm:text-3xl" />
            ) : (
              <MdAccessTime style={{ color: `${primaryColor}60` }} size={24} className="sm:text-3xl" />
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-xs sm:text-sm" style={{ color: primaryColor }}>
              No items yet
            </p>
            <p className="text-[10px] sm:text-xs opacity-50">
              Start shopping to see {type === 'frequent' ? 'frequent' : 'recent'} items
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
function OrderHistoryPage() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingReviewModal, setBookingReviewModal] = useState({
    isOpen: false,
    booking: null,
  });
  const [bookingReviewForm, setBookingReviewForm] = useState({
    rating: 5,
    title: "",
    comment: "",
  });
  const [bookingReviewSubmitting, setBookingReviewSubmitting] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [trackingData, setTrackingData] = useState({});
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const { businessName: urlBusinessName, bussinessUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, loading: themeLoading } = useTheme(bussinessUuid);
  const isDark = isDarkTheme(theme);
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) || businessData[0]
    : businessData;
  const displayBusinessName = urlBusinessName || business?.businessName || "Store";
  const isB2BMode = localStorage.getItem('b2b_Price') === 'true';
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    product: null,
    order: null
  });

  const [customerProfile, setCustomerProfile] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [productReviews, setProductReviews] = useState({});

  const [reviewedProductsCache, setReviewedProductsCache] = useState({});

  const [frequentProducts, setFrequentProducts] = useState([]);
  const [mostRecentProduct, setMostRecentProduct] = useState(null);

  const handleBackNavigation = () => {
    const stateFrom = location?.state?.from;
    if (stateFrom && stateFrom !== location.pathname) {
      navigate(stateFrom);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(
      getStorefrontPath({
        businessName: displayBusinessName,
        bussinessUuid,
      }),
      { replace: true }
    );
  };
  const normalizePhone = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  // Load customer profile on component mount
  useEffect(() => {
    const loadCustomerProfile = () => {
      try {
        setCustomerLoading(true);

        const savedUser = localStorage.getItem(`user_${bussinessUuid}`);
        const savedGuest = localStorage.getItem(`guest_${bussinessUuid}`);

        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);

            if (parsedUser && (parsedUser._id || parsedUser.id || parsedUser.customer_mobile)) {
              if (!parsedUser.isGuest) parsedUser.isGuest = false;
              if (!parsedUser.isCustomer && !parsedUser.isGuest) parsedUser.isCustomer = true;

              setCustomerProfile(parsedUser);
            } else {
              setCustomerProfile(null);
            }
          } catch (err) {
            console.error("Error parsing user data:", err);
            localStorage.removeItem(`user_${bussinessUuid}`);
            setCustomerProfile(null);
          }
        } else if (savedGuest) {
          try {
            const parsedGuest = JSON.parse(savedGuest);

            if (parsedGuest) {
              parsedGuest.isGuest = true;
              parsedGuest.isCustomer = false;
              setCustomerProfile(parsedGuest);
            } else {
              setCustomerProfile(null);
            }
          } catch (err) {
            console.error("Error parsing guest data:", err);
            localStorage.removeItem(`guest_${bussinessUuid}`);
            setCustomerProfile(null);
          }
        } else {
          setCustomerProfile(null);
        }
      } catch (error) {
        console.error('Error loading customer profile:', error);
        setCustomerProfile(null);
      } finally {
        setCustomerLoading(false);
      }
    };

    loadCustomerProfile();
  }, [bussinessUuid]);

  const handleProductClick = (product) => {
    if (product.productId) {
      navigate(
        getStorefrontPath({
          businessName: displayBusinessName,
          bussinessUuid,
          suffix: `product/${product.productId}`,
        }),
      );
    }
  };

  const extractFeaturedProducts = (ordersList) => {
    if (!ordersList || ordersList.length === 0) {
      setFrequentProducts([]);
      setMostRecentProduct(null);
      return;
    }

    const deliveredOrders = ordersList.filter(order =>
      order.orderStatus?.toLowerCase() === 'delivered' ||
      order.deliveryStatus?.toLowerCase() === 'delivered' ||
      order.status?.toLowerCase() === 'delivered'
    );

    if (deliveredOrders.length === 0) {
      setFrequentProducts([]);
      setMostRecentProduct(null);
      return;
    }

    const sortedOrders = [...deliveredOrders].sort((a, b) => {
      const dateA = a.orderDate || a.createdAt;
      const dateB = b.orderDate || b.createdAt;
      return new Date(dateB) - new Date(dateA);
    });

    if (sortedOrders.length > 0 && sortedOrders[0].items && sortedOrders[0].items.length > 0) {
      const mostRecentItem = sortedOrders[0].items[0];
      setMostRecentProduct({
        ...mostRecentItem,
        purchaseDate: sortedOrders[0].orderDate || sortedOrders[0].createdAt,
        orderId: sortedOrders[0].orderId,
        image: mostRecentItem.itemPicture || mostRecentItem.image,
      });
    } else {
      setMostRecentProduct(null);
    }

    const productFrequency = {};
    deliveredOrders.forEach(order => {
      if (order.items) {
        order.items.forEach(item => {
          if (!item.productId) return;

          const key = item.variantId
            ? `${item.productId}_${item.variantId}`
            : item.productId;

          if (!productFrequency[key]) {
            productFrequency[key] = {
              ...item,
              productId: item.productId,
              productName: item.productName,
              variantId: item.variantId || null,
              variantName: item.variantName || null,
              variantAttributes: item.variantAttributes || [],
              hasVariants: item.hasVariants || false,
              count: 0,
              lastPurchased: null,
              image: item.itemPicture || item.image,
              salePrice: parseFloat(item.salePrice || item.price || 0),
            };
          }

          productFrequency[key].count += parseInt(item.cartQuantity || item.quantity || 1);

          const orderDate = order.orderDate || order.createdAt;
          if (!productFrequency[key].lastPurchased ||
            new Date(orderDate) > new Date(productFrequency[key].lastPurchased)) {
            productFrequency[key].lastPurchased = orderDate;
          }
        });
      }
    });

    const frequentProductsList = Object.values(productFrequency)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    setFrequentProducts(frequentProductsList);
  };

  const fetchOrders = async () => {
    try {
      setPageLoading(true);

      setReviewedProductsCache({});

      let customerIdentifier = customerProfile.customerId;

      if (!customerIdentifier) {
        customerIdentifier = customerProfile._id;
      }

      if (!customerIdentifier) {
        setError(t("STORENEWDETAILS.ORDER_HISTORY.UNABLE_TO_LOAD_MESSAGE"));
        setPageLoading(false);
        return;
      }

      const res = await getAllUserOrders(customerIdentifier, bussinessUuid);

      if (res && res.data) {
        const ordersArray = Array.isArray(res.data) ? res.data : [];

        if (ordersArray.length > 0) {
          setOrders(ordersArray);
          extractFeaturedProducts(ordersArray);
          await loadReviewsForDeliveredProducts(ordersArray);
          setError("");
        } else {
          setOrders([]);
          setFrequentProducts([]);
          setMostRecentProduct(null);
          setError(t("STORENEWDETAILS.ORDER_HISTORY.NO_ORDERS_MESSAGE"));
        }
      } else {
        setOrders([]);
        setFrequentProducts([]);
        setMostRecentProduct(null);
        setError(t("STORENEWDETAILS.ORDER_HISTORY.NO_ORDERS_MESSAGE"));
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(`Failed to load orders: ${error.message || 'Unknown error'}`);
      setOrders([]);
      setFrequentProducts([]);
      setMostRecentProduct(null);
    } finally {
      setPageLoading(false);
    }
  };

  const fetchBookings = async () => {
    if (!customerProfile || !bussinessUuid) return;

    try {
      setBookingsLoading(true);

      const mobileFromProfile =
        customerProfile?.customer_mobile ||
        customerProfile?.phone ||
        customerProfile?.mobile ||
        "";
      const emailFromProfile = customerProfile?.email || "";
      const nameFromProfile = customerProfile?.name || customerProfile?.customerName || "";

      const filters = {};
      const customerIdFromProfile =
        customerProfile?.customerId || customerProfile?._id || customerProfile?.id || "";
      if (String(customerIdFromProfile).trim()) {
        filters.customerId = String(customerIdFromProfile).trim();
      }
      if (normalizePhone(mobileFromProfile)) {
        filters.mobile = normalizePhone(mobileFromProfile);
      }
      if (String(emailFromProfile).trim()) {
        filters.email = String(emailFromProfile).trim().toLowerCase();
      }
      if (String(nameFromProfile).trim()) {
        filters.name = String(nameFromProfile).trim();
      }

      const response = await getBookings(bussinessUuid, filters);
      const bookingList = Array.isArray(response) ? response : [];
      const sortedBookings = [...bookingList].sort((a, b) => {
        const timeA = new Date(a?.createdAt || a?.date || 0).getTime();
        const timeB = new Date(b?.createdAt || b?.date || 0).getTime();
        return timeB - timeA;
      });

      setBookings(sortedBookings);
    } catch (bookingError) {
      console.error("Error fetching booking history:", bookingError);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  const canReviewBooking = (booking) => {
    if (!booking || !customerProfile || customerProfile.isGuest) return false;
    const status = String(booking.status || "").toLowerCase();
    if (status !== "completed") return false;
    return !booking.reviewSubmittedAt;
  };

  const openBookingReviewModal = (booking) => {
    if (!canReviewBooking(booking)) return;

    setBookingReviewForm({
      rating: 5,
      title: "",
      comment: "",
    });

    setBookingReviewModal({
      isOpen: true,
      booking,
    });
  };

  const closeBookingReviewModal = () => {
    if (bookingReviewSubmitting) return;
    if (bookingReviewModal?.booking?._id) {
      sessionStorage.setItem(
        `service_review_popup_dismissed_${bookingReviewModal.booking._id}`,
        "1"
      );
    }
    setBookingReviewModal({
      isOpen: false,
      booking: null,
    });
  };

  const handleSubmitBookingReview = async () => {
    const activeBooking = bookingReviewModal.booking;
    if (!activeBooking?._id) return;

    const normalizedComment = String(bookingReviewForm.comment || "").trim();
    if (normalizedComment.length < 5) {
      toast.error("Please enter at least 5 characters in your review.");
      return;
    }

    setBookingReviewSubmitting(true);
    try {
      const customerId =
        customerProfile?.customerId || customerProfile?._id || customerProfile?.id || "";

      const response = await submitBookingReview(bussinessUuid, activeBooking._id, {
        rating: bookingReviewForm.rating,
        title: bookingReviewForm.title,
        comment: normalizedComment,
        customerId,
      });

      const updatedBooking = response?.booking;
      if (updatedBooking?._id) {
        setBookings((prev) =>
          prev.map((booking) => (booking._id === updatedBooking._id ? updatedBooking : booking))
        );
      }

      toast.success("Thanks for sharing your service experience.");
      closeBookingReviewModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit service review.");
    } finally {
      setBookingReviewSubmitting(false);
    }
  };

  useEffect(() => {
    if (!customerLoading && customerProfile) {
      fetchOrders();
      fetchBookings();
    } else if (!customerLoading && !customerProfile) {
      setPageLoading(false);
      setBookings([]);
      setError(t("STORENEWDETAILS.ORDER_HISTORY.LOGIN_MESSAGE"));
    }
  }, [customerLoading, customerProfile, bussinessUuid]);

  const intervalRef = useRef();
  useEffect(() => {
    if (!customerLoading && customerProfile) {
      intervalRef.current = setInterval(() => {
        fetchOrders();
        fetchBookings();
      }, 60000);
      return () => clearInterval(intervalRef.current);
    }
  }, [customerLoading, customerProfile, bussinessUuid]);

  useEffect(() => {
    if (!customerProfile || customerProfile.isGuest || !bookings.length) return;
    if (bookingReviewModal.isOpen) return;

    const pendingBooking = bookings.find((booking) => canReviewBooking(booking));
    if (!pendingBooking) return;

    const popupDismissKey = `service_review_popup_dismissed_${pendingBooking._id}`;
    if (sessionStorage.getItem(popupDismissKey) === "1") {
      return;
    }

    openBookingReviewModal(pendingBooking);
  }, [bookings, customerProfile, bookingReviewModal.isOpen]);

  const getBookingStatusClass = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "confirmed") return "bg-green-100 text-green-800 border border-green-200";
    if (normalized === "in progress" || normalized === "in_progress" || normalized === "inprogress") return "bg-indigo-100 text-indigo-800 border border-indigo-200";
    if (normalized === "cancelled") return "bg-red-100 text-red-800 border border-red-200";
    if (normalized === "completed") return "bg-blue-100 text-blue-800 border border-blue-200";
    return "bg-yellow-100 text-yellow-800 border border-yellow-200";
  };

  const getBookingSortTimestamp = (booking) => {
    const datePart = String(booking?.date || "").trim();
    const timePart = String(booking?.time || "").trim();
    const candidate = datePart
      ? `${datePart}${timePart ? `T${timePart.length <= 5 ? `${timePart}:00` : timePart}` : ""}`
      : booking?.createdAt;

    const parsed = new Date(candidate || 0).getTime();
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }

    const fallback = new Date(booking?.createdAt || 0).getTime();
    return Number.isNaN(fallback) ? 0 : fallback;
  };

  const groupedBookingsByDate = useMemo(() => {
    const groups = new Map();

    bookings.forEach((booking) => {
      const key = String(booking?.date || "").trim() || "No date";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(booking);
    });

    const entries = [...groups.entries()].map(([dateKey, items]) => ({
      dateKey,
      items: [...items].sort((a, b) => getBookingSortTimestamp(b) - getBookingSortTimestamp(a)),
    }));

    return entries.sort((a, b) => {
      const aTime = new Date(a.dateKey).getTime();
      const bTime = new Date(b.dateKey).getTime();

      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return bTime - aTime;
    });
  }, [bookings]);

  const loadReviewsForDeliveredProducts = async (ordersList) => {
    if (!customerProfile || customerProfile.isGuest) return;

    const customerId = customerProfile._id || customerProfile.id || customerProfile.customerId;
    if (!customerId) return;

    const newReviewsMap = {};
    const newCache = {};

    for (const order of ordersList) {
      const isDelivered =
        order.orderStatus?.toLowerCase() === 'delivered' ||
        order.deliveryStatus?.toLowerCase() === 'delivered' ||
        order.status?.toLowerCase() === 'delivered';

      if (isDelivered && order.items && Array.isArray(order.items)) {
        for (const item of order.items) {
          const productId = item.productId || item._id;
          if (!productId) continue;

          try {
            let reviewResponse = null;

            try {
              reviewResponse = await reviewService.getCustomerProductReview(
                bussinessUuid,
                productId,
                customerId
              );
            } catch (err) {
              console.log(`Method 1 failed for ${productId}:`, err.message);
            }

            if (!reviewResponse || !reviewResponse.data) {
              try {
                const canReviewResponse = await reviewService.canReviewProduct(
                  bussinessUuid,
                  productId,
                  customerId
                );

                if (canReviewResponse && canReviewResponse.canReview === false &&
                  canReviewResponse.hasReviewed === true) {
                  reviewResponse = {
                    data: {
                      _id: `existing_${Date.now()}`,
                      productId: productId,
                      rating: 0,
                      comment: "Review exists",
                      status: "existing"
                    }
                  };
                }
              } catch (err) {
                console.log(`Method 2 failed for ${productId}:`, err.message);
              }
            }

            if (reviewResponse && reviewResponse.data) {
              newReviewsMap[productId] = reviewResponse.data;
              newCache[productId] = true;

              setOrders(prevOrders =>
                prevOrders.map(prevOrder => {
                  if (prevOrder._id === order._id) {
                    return {
                      ...prevOrder,
                      items: prevOrder.items?.map(prevItem => {
                        if ((prevItem.productId || prevItem._id) === productId) {
                          return {
                            ...prevItem,
                            hasReview: true,
                            reviewId: reviewResponse.data._id,
                            reviewData: reviewResponse.data
                          };
                        }
                        return prevItem;
                      })
                    };
                  }
                  return prevOrder;
                })
              );
            }
          } catch (error) {
            console.error('Error loading review for product:', productId, error);
          }
        }
      }
    }

    setProductReviews(prev => ({ ...prev, ...newReviewsMap }));
    setReviewedProductsCache(prev => ({ ...prev, ...newCache }));
  };

  const isProductReviewed = (product) => {
    const productId = product.productId || product._id;
    if (!productId) return false;

    if (reviewedProductsCache[productId]) return true;
    if (productReviews[productId]) return true;
    if (product.reviewId) return true;
    if (product.hasReview === true) return true;
    if (product.reviewData) return true;

    try {
      const cacheKey = `reviewed_${bussinessUuid}_${customerProfile?._id || 'guest'}_${productId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached === 'true') return true;
    } catch (e) {
      // Ignore localStorage errors
    }

    return false;
  };

  const canReviewProduct = (product, order) => {
    if (!customerProfile || customerProfile.isGuest) return false;

    const isDelivered =
      order.orderStatus?.toLowerCase() === 'delivered' ||
      order.deliveryStatus?.toLowerCase() === 'delivered' ||
      order.status?.toLowerCase() === 'delivered';

    if (!isDelivered) return false;
    if (isProductReviewed(product)) return false;

    return true;
  };

  const handleOpenReviewModal = (product, order) => {
    if (!canReviewProduct(product, order)) {
      toast.info(t("STORENEWDETAILS.ORDER_HISTORY.ALREADY_REVIEWED"));
      return;
    }

    setReviewModal({
      isOpen: true,
      product,
      order
    });
  };

  const handleReviewSubmitted = async (reviewData) => {
    try {
      if (reviewData.product) {
        const productId = reviewData.product.productId || reviewData.product._id;

        const permanentReview = {
          _id: reviewData.review.reviewId || `perm_${Date.now()}`,
          productId: productId,
          rating: reviewData.review.rating,
          comment: reviewData.review.comment,
          title: reviewData.review.title,
          photosCount: reviewData.review.photosCount || 0,
          createdAt: new Date().toISOString(),
          status: "approved",
          isVerifiedPurchase: true
        };

        setProductReviews(prev => ({
          ...prev,
          [productId]: permanentReview
        }));

        setReviewedProductsCache(prev => ({
          ...prev,
          [productId]: true
        }));

        try {
          const cacheKey = `reviewed_${bussinessUuid}_${customerProfile?._id || 'guest'}_${productId}`;
          localStorage.setItem(cacheKey, 'true');
          localStorage.setItem(`${cacheKey}_data`, JSON.stringify(permanentReview));
        } catch (e) {
          console.error('Error saving to localStorage:', e);
        }

        setOrders(prevOrders =>
          prevOrders.map(order => {
            if (order._id === reviewModal.order?._id) {
              return {
                ...order,
                items: order.items?.map(item => {
                  if ((item.productId || item._id) === productId) {
                    return {
                      ...item,
                      hasReview: true,
                      reviewId: permanentReview._id,
                      reviewData: permanentReview
                    };
                  }
                  return item;
                })
              };
            }
            return order;
          })
        );

        toast.success(t("STORENEWDETAILS.REVIEW_MODAL.SUCCESS"));
      }

      setReviewModal({
        isOpen: false,
        product: null,
        order: null
      });

    } catch (error) {
      console.error('Error updating review state:', error);
      toast.error('Failed to update review status');
    }
  };

  const handleLoginRedirect = () => {
    navigate(
      getStorefrontPath({
        businessName: displayBusinessName,
        bussinessUuid,
      }),
    );
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const refreshTracking = async (orderId) => {
    const order = orders.find((o) => o.orderId === orderId);
    if (!order?.logistics?.tracking_number) return;

    try {
      if (isMockTrackingNumber(order.logistics.tracking_number)) {
        const currentTracking = trackingData[orderId] || {};
        const updatedTracking = {
          ...currentTracking,
          last_updated: new Date().toISOString(),
        };
        setTrackingData((prev) => ({
          ...prev,
          [orderId]: updatedTracking,
        }));
        return;
      }

      const response = await trackShipment(
        bussinessUuid,
        orderId
      );

      if (response.data.success) {
        setTrackingData((prev) => ({
          ...prev,
          [orderId]: {
            ...response.data.tracking,
            source: response.data.source || "real_api",
          },
        }));
        fetchOrders();
      }
    } catch (error) {
      console.error("Error refreshing tracking:", error);
    }
  };

  const isMockTrackingNumber = (trackingNumber) => {
    const mockPatterns = [
      /^XPB[A-Z0-9]{9}IN$/,
      /^DEL[A-Z0-9]{9}IN$/,
      /^BLD[A-Z0-9]{9}IN$/,
      /^DTDC[A-Z0-9]{9}IN$/,
      /^ECOM[A-Z0-9]{9}IN$/,
      /^SFX[A-Z0-9]{9}IN$/,
      /^INP[A-Z0-9]{9}IN$/,
      /^FDX[A-Z0-9]{9}IN$/,
      /^UPS[A-Z0-9]{9}IN$/,
      /^TRK[A-Z0-9]{9}IN$/,
    ];

    return mockPatterns.some((pattern) => pattern.test(trackingNumber));
  };

  const generateEnhancedMockTrackingData = (order) => {
    if (!order.logistics) return null;

    const baseTime = new Date(
      order.logistics.createdAt || order.createdAt || new Date()
    );
    const validBaseTime = isNaN(baseTime.getTime()) ? new Date() : baseTime;

    const trackingHash = order.logistics.tracking_number
      .split("")
      .reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);

    const scenarioIndex = Math.abs(trackingHash) % 5;

    const scenarios = [
      {
        name: "recent",
        status: "label_created",
        events: [
          {
            status: "label_created",
            description: "Shipping label created, awaiting pickup",
            location: "Mumbai Warehouse",
            timestamp: new Date(
              validBaseTime.getTime() - 2 * 60 * 60 * 1000
            ).toISOString(),
          },
        ],
      },
      {
        name: "picked_up",
        status: "picked_up",
        events: [
          {
            status: "label_created",
            description: "Shipping label created",
            location: "Mumbai Warehouse",
            timestamp: new Date(
              validBaseTime.getTime() - 8 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "picked_up",
            description: "Package picked up by carrier",
            location: "Mumbai, Maharashtra",
            timestamp: new Date(
              validBaseTime.getTime() - 4 * 60 * 60 * 1000
            ).toISOString(),
          },
        ],
      },
      {
        name: "in_transit",
        status: "in_transit",
        events: [
          {
            status: "label_created",
            description: "Shipping label created",
            location: "Mumbai Warehouse",
            timestamp: new Date(
              validBaseTime.getTime() - 36 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "picked_up",
            description: "Package picked up by carrier",
            location: "Mumbai, Maharashtra",
            timestamp: new Date(
              validBaseTime.getTime() - 32 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "in_transit",
            description: "Package in transit to destination hub",
            location: "Mumbai Sorting Facility",
            timestamp: new Date(
              validBaseTime.getTime() - 28 * 60 * 60 * 1000
            ).toISOString(),
          },
        ],
      },
      {
        name: "out_for_delivery",
        status: "out_for_delivery",
        events: [
          {
            status: "label_created",
            description: "Shipping label created",
            location: "Mumbai Warehouse",
            timestamp: new Date(
              validBaseTime.getTime() - 72 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "picked_up",
            description: "Package picked up by carrier",
            location: "Mumbai, Maharashtra",
            timestamp: new Date(
              validBaseTime.getTime() - 68 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "in_transit",
            description: "Package in transit to destination",
            location: "Mumbai Sorting Facility",
            timestamp: new Date(
              validBaseTime.getTime() - 64 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "out_for_delivery",
            description: "Package out for delivery",
            location: "Local Delivery Station",
            timestamp: new Date(
              validBaseTime.getTime() - 4 * 60 * 60 * 1000
            ).toISOString(),
          },
        ],
      },
      {
        name: "delivered",
        status: "delivered",
        events: [
          {
            status: "label_created",
            description: "Shipping label created",
            location: "Mumbai Warehouse",
            timestamp: new Date(
              validBaseTime.getTime() - 96 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "picked_up",
            description: "Package picked up by carrier",
            location: "Mumbai, Maharashtra",
            timestamp: new Date(
              validBaseTime.getTime() - 92 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "in_transit",
            description: "Package in transit to destination",
            location: "Mumbai Sorting Facility",
            timestamp: new Date(
              validBaseTime.getTime() - 88 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "out_for_delivery",
            description: "Package out for delivery",
            location: "Local Delivery Station",
            timestamp: new Date(
              validBaseTime.getTime() - 8 * 60 * 60 * 1000
            ).toISOString(),
          },
          {
            status: "delivered",
            description: "Package delivered successfully",
            location: "Customer Premises",
            timestamp: new Date(
              validBaseTime.getTime() - 2 * 60 * 60 * 1000
            ).toISOString(),
          },
        ],
      },
    ];

    const selectedScenario = scenarios[scenarioIndex];

    return {
      tracking_number: order.logistics.tracking_number,
      carrier: order.logistics.carrier,
      carrier_name: order.logistics.carrier_name,
      status: selectedScenario.status,
      events: selectedScenario.events,
      estimated_delivery: order.logistics.estimated_delivery,
      last_updated: new Date().toISOString(),
      source: "enhanced_mock",
    };
  };

  useEffect(() => {
    const fetchAllTracking = async () => {
      const ordersWithLogistics = orders.filter((order) => {
        return order.logistics && order.logistics.tracking_number;
      });

      const trackingPromises = ordersWithLogistics.map(async (order) => {
        try {
          const trackingNumber = order.logistics.tracking_number;
          const carrier = order.logistics.carrier || "delhivery";

          if (isMockTrackingNumber(trackingNumber)) {
            const mockData = generateEnhancedMockTrackingData(order);
            return { [order.orderId]: mockData };
          }

          let response;
          try {
            response = await trackRealtime(
              bussinessUuid,
              trackingNumber,
              carrier,
            );

            if (response.data.success) {
              return {
                [order.orderId]: {
                  ...response.data.tracking,
                  source: response.data.source || "real_api",
                },
              };
            }
          } catch (apiError) {
            const mockData = generateEnhancedMockTrackingData(order);
            return { [order.orderId]: mockData };
          }

          const mockData = generateEnhancedMockTrackingData(order);
          return { [order.orderId]: mockData };
        } catch (error) {
          const mockData = generateEnhancedMockTrackingData(order);
          return { [order.orderId]: mockData };
        }
      });

      const results = await Promise.all(trackingPromises);
      const trackingMap = Object.assign({}, ...results);
      setTrackingData(trackingMap);
    };

    if (orders.length > 0) {
      fetchAllTracking();
    }
  }, [orders, bussinessUuid]);

  if (themeLoading || pageLoading || customerLoading) {
    return <LogoLoading/>;
  }

  if (error && !customerProfile) {
    return (
      <div
        className='min-h-screen flex items-center justify-center px-3 sm:px-4'
        style={{
          backgroundColor: theme?.backgroundColor || '#f9fafb',
          fontFamily: theme?.fontFamily || 'Inter',
        }}
      >
        <ThemedCard className='p-6 sm:p-8 text-center max-w-md mx-auto' theme={theme}>
          <div
            className='w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-red-100'
            style={{ borderRadius: theme?.borderRadius || '9999px' }}
          >
            <MdErrorOutline className='text-red-500 text-xl sm:text-2xl' />
          </div>
          <h3
            className='text-base sm:text-lg font-bold mb-1.5 sm:mb-2'
            style={{
              color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
              fontFamily: theme?.fontFamily || 'Inter',
            }}
          >
            {t("STORENEWDETAILS.ORDER_HISTORY.UNABLE_TO_LOAD")}
          </h3>
          <p
            className='mb-4 sm:mb-6 text-xs sm:text-sm'
            style={{
              color: theme?.textColor + '80' || '#6b7280',
              fontFamily: theme?.fontFamily || 'Inter',
            }}
          >
            {error}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                  }),
                )
              }
              className='w-full px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-200'
              style={{
                backgroundColor: theme?.buttonColor || '#4f46e5',
                color: '#ffffff',
                borderRadius: theme?.buttonStyle === 'pill' ? '9999px' :
                  theme?.buttonStyle === 'rounded' ? '8px' : '4px',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.BACK")}
            </button>
            <button
              onClick={handleLoginRedirect}
              className='w-full px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-200 border'
              style={{
                borderColor: theme?.primaryColor || '#4f46e5',
                color: theme?.primaryColor || '#4f46e5',
                backgroundColor: 'transparent',
                borderRadius: theme?.borderRadius || '8px',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.LOGIN")}
            </button>
          </div>
        </ThemedCard>
      </div>
    );
  }

  return (
    <div
      className='min-h-screen'
      style={{
        backgroundColor: theme?.backgroundColor || '#f9fafb',
        fontFamily: theme?.fontFamily || 'Inter',
      }}
    >
      <CustomerProfileBanner
        customerProfile={customerProfile}
        theme={theme}
        onLogin={handleLoginRedirect}
      />

      {/* Header - RESPONSIVE */}
      <div
        className='shadow-sm border-b'
        style={{
          backgroundColor: theme?.backgroundColor || (isDark ? '#1f2937' : '#ffffff'),
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <div className='max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2 sm:space-x-4'>
              <button
                onClick={handleBackNavigation}
                className='flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all duration-200 text-xs sm:text-sm font-medium'
                style={{
                  color: theme?.primaryColor || '#4f46e5',
                  fontFamily: theme?.fontFamily || 'Inter',
                }}
              >
                <MdArrowBack className='text-sm sm:text-lg' />
                <span className="hidden xs:inline">{t("STORENEWDETAILS.ORDER_HISTORY.BACK")}</span>
              </button>
            </div>
            <div className='flex items-center gap-2 sm:gap-3'>
              <div
                className='p-2 sm:p-3 rounded-lg sm:rounded-xl'
                style={{
                  backgroundColor: theme?.primaryColor || '#4f46e5',
                  borderRadius: theme?.borderRadius || '12px',
                }}
              >
                <MdHistory className='text-white text-lg sm:text-xl' />
              </div>
              <div>
                <h1
                  className='text-lg sm:text-xl md:text-2xl font-bold'
                  style={{
                    color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                    fontFamily: theme?.fontFamily || 'Inter',
                  }}
                >
                  {t("STORENEWDETAILS.ORDER_HISTORY.TITLE")}
                </h1>
                <p
                  className='text-[10px] sm:text-sm hidden sm:block'
                  style={{
                    color: theme?.textColor + '80' || '#6b7280',
                    fontFamily: theme?.fontFamily || 'Inter',
                  }}
                >
                  {t("STORENEWDETAILS.ORDER_HISTORY.SUBTITLE")}
                </p>
              </div>
            </div>
            <div className='w-10 sm:w-20'></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6'>
        {/* Featured Products */}
        {customerProfile && (frequentProducts.length > 0 || mostRecentProduct) && (
          <FeaturedProductsCard
            frequentProducts={frequentProducts}
            recentProduct={mostRecentProduct}
            theme={theme}
            onProductClick={handleProductClick}
          />
        )}
        <ThemedCard className='p-4 sm:p-5 mb-4 sm:mb-6' theme={theme}>
          <div className='flex items-center justify-between mb-3'>
            <h3
              className='text-base sm:text-lg font-bold flex items-center gap-2'
              style={{
                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              <MdSchedule style={{ color: theme?.primaryColor || '#4f46e5' }} />
              Service Booking History
            </h3>
            <span
              className='text-xs px-2 py-1 rounded-full'
              style={{
                backgroundColor: theme?.primaryColor ? `${theme.primaryColor}14` : '#eef2ff',
                color: theme?.primaryColor || '#4f46e5',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {bookings.length} booking{bookings.length === 1 ? '' : 's'}
            </span>
          </div>

          {bookingsLoading ? (
            <p
              className='text-sm'
              style={{
                color: theme?.textColor + '90' || '#6b7280',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              Loading booking history...
            </p>
          ) : bookings.length > 0 ? (
            <div className='space-y-3'>
              {groupedBookingsByDate.map((group) => (
                <div
                  key={group.dateKey}
                  className='rounded-lg border p-3 sm:p-4'
                  style={{
                    borderColor: isDark ? '#334155' : '#dbe4ef',
                    backgroundColor: isDark ? '#0b1220' : '#f8fafc',
                  }}
                >
                  <div className='mb-3 flex items-center justify-between'>
                    <h4
                      className='text-sm sm:text-base font-bold'
                      style={{
                        color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                        fontFamily: theme?.fontFamily || 'Inter',
                      }}
                    >
                      {group.dateKey === 'No date' ? 'Date unavailable' : formatDate(group.dateKey)}
                    </h4>
                    <span
                      className='text-[11px] px-2 py-1 rounded-full'
                      style={{
                        backgroundColor: theme?.primaryColor ? `${theme.primaryColor}14` : '#eef2ff',
                        color: theme?.primaryColor || '#4f46e5',
                      }}
                    >
                      {group.items.length} booking{group.items.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  <div className='space-y-3'>
                    {group.items.map((booking) => (
                      <div
                        key={booking?._id}
                        className='rounded-lg border p-3 sm:p-4'
                        style={{
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                        }}
                      >
                        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2'>
                          <div className='flex-1'>
                            <p
                              className='font-semibold text-sm sm:text-base'
                              style={{
                                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              {booking?.serviceName || 'Service'}
                            </p>
                            <p
                              className='text-xs sm:text-sm mt-0.5'
                              style={{
                                color: theme?.textColor + '80' || '#6b7280',
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              Booking ID: {booking?.bookingId || booking?._id?.slice(-6)}
                            </p>
                          </div>
                          <div className='flex items-center gap-2 flex-wrap'>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium w-fit ${getBookingStatusClass(booking?.status)}`}>
                              {booking?.status || 'Pending'}
                            </span>

                            {/* Invoice buttons for completed bookings */}
                            {booking?.status?.toLowerCase() === "completed" && (
                              <>
                                <button
                                  onClick={() => {
                                    navigate(
                                      getStorefrontPath({
                                        businessName: displayBusinessName,
                                        bussinessUuid,
                                        suffix: `service-invoice/${booking._id}`,
                                      })
                                    );
                                  }}
                                  className='flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors hover:opacity-90'
                                  style={{
                                    backgroundColor: theme?.primaryColor || '#4f46e5',
                                    color: '#ffffff',
                                    borderRadius: theme?.borderRadius || '8px',
                                  }}
                                >
                                  <MdReceipt className='text-xs' />
                                  Invoice
                                </button>

                                <button
                                  onClick={() => {
                                    navigate(
                                      getStorefrontPath({
                                        businessName: displayBusinessName,
                                        bussinessUuid,
                                        suffix: `service-invoice/${booking._id}`,
                                      }) + '?download=pdf'
                                    );
                                  }}
                                  className='flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors hover:opacity-90'
                                  style={{
                                    backgroundColor: theme?.secondaryColor || '#10b981',
                                    color: '#ffffff',
                                    borderRadius: theme?.borderRadius || '8px',
                                  }}
                                >
                                  <MdDownload className='text-xs' />
                                  PDF
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs sm:text-sm'>
                          <p style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                            <span className='font-medium'>Slot:</span> {booking?.date ? formatDate(booking.date) : '-'} {booking?.time || ''}
                          </p>
                          <p style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                            <span className='font-medium'>Amount:</span> ₹{Number(booking?.amount || 0).toFixed(2)}
                          </p>
                          <p style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                            <span className='font-medium'>Payment:</span> {booking?.paymentMode || 'N/A'}
                          </p>
                          <p style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                            <span className='font-medium'>Expert:</span> {booking?.employeeName || 'Will be assigned'}
                          </p>
                          {booking?.employeeChangeReason ? (
                            <p className='sm:col-span-2 lg:col-span-4' style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                              <span className='font-medium'>Employee change reason:</span> {booking.employeeChangeReason}
                              {booking?.employeeChangedAt ? ` • ${formatDateTime(booking.employeeChangedAt)}` : ''}
                            </p>
                          ) : null}
                        </div>

                        <div className='mt-3 flex flex-col gap-2'>
                          {booking?.reviewSubmittedAt ? (
                            <div className='rounded-lg border p-2.5 text-xs sm:text-sm'
                              style={{
                                borderColor: isDark ? '#334155' : '#e2e8f0',
                                backgroundColor: isDark ? '#0b1220' : '#f8fafc',
                              }}
                            >
                              <p className='font-semibold mb-1' style={{ color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827') }}>
                                Your service review
                              </p>
                              <p style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                                {renderStars(Number(booking?.reviewRating || 0), 'text-sm')}
                              </p>
                              {booking?.reviewComment ? (
                                <p className='mt-1' style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                                  {booking.reviewComment}
                                </p>
                              ) : null}
                            </div>
                          ) : canReviewBooking(booking) ? (
                            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2'>
                              <p className='text-xs sm:text-sm' style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                                Your service is completed. Share your experience.
                              </p>
                              <button
                                type='button'
                                onClick={() => openBookingReviewModal(booking)}
                                className='px-3 py-1.5 rounded-lg text-xs font-semibold'
                                style={{
                                  backgroundColor: theme?.buttonColor || '#4f46e5',
                                  color: '#ffffff',
                                }}
                              >
                                Rate service
                              </button>
                            </div>
                          ) : (
                            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2'>
                              <p className='text-xs sm:text-sm' style={{ color: theme?.textColor || (isDark ? '#d1d5db' : '#374151') }}>
                                Review opens after this booking is completed.
                              </p>
                              <button
                                type='button'
                                disabled
                                className='px-3 py-1.5 rounded-lg text-xs font-semibold cursor-not-allowed opacity-60'
                                style={{
                                  backgroundColor: theme?.buttonColor || '#4f46e5',
                                  color: '#ffffff',
                                }}
                              >
                                Rate service
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p
              className='text-sm'
              style={{
                color: theme?.textColor + '90' || '#6b7280',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              No service bookings found yet.
            </p>
          )}
        </ThemedCard>

        {/* Orders List */}
        {error && customerProfile ? (
          <ThemedCard className='p-6 sm:p-8 text-center max-w-md mx-auto' theme={theme}>
            <div
              className='w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 bg-red-100'
              style={{ borderRadius: theme?.borderRadius || '9999px' }}
            >
              <MdErrorOutline className='text-red-500 text-xl sm:text-2xl' />
            </div>
            <h3
              className='text-base sm:text-lg font-bold mb-1.5 sm:mb-2'
              style={{
                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.UNABLE_TO_LOAD")}
            </h3>
            <p
              className='mb-4 sm:mb-6 text-xs sm:text-sm'
              style={{
                color: theme?.textColor + '80' || '#6b7280',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {error}
            </p>
            <button
              onClick={fetchOrders}
              className='w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-200'
              style={{
                backgroundColor: theme?.buttonColor || '#4f46e5',
                color: '#ffffff',
                borderRadius: theme?.buttonStyle === 'pill' ? '9999px' :
                  theme?.buttonStyle === 'rounded' ? '8px' : '4px',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.TRY_AGAIN")}
            </button>
          </ThemedCard>
        ) : orders && orders.length > 0 ? (
          <div className='space-y-4 sm:space-y-6'>
            {orders.map((order) => {
              const tracking = trackingData[order.orderId];
              const hasTracking = order.logistics && tracking;
              const isExpanded = expandedOrders[order._id];
              const isMockNumber = hasTracking && isMockTrackingNumber(tracking.tracking_number);
              const revisedDeliverySlot = formatDeliverySlot(
                order.customerUpdate?.revisedDeliveryDate || order.revisedDeliveryDate,
                order.customerUpdate?.revisedDeliveryTime || order.revisedDeliveryTime,
              );
              const isDeliveryUpdated = order.customerUpdate?.type === 'delayed';
              const customerUpdateTitle = isDeliveryUpdated
                ? 'Delivery schedule updated'
                : (order.customerUpdate?.title || 'Order update');
              const customerUpdateMessage = isDeliveryUpdated
                ? (revisedDeliverySlot
                  ? `Your delivery time has been updated to ${revisedDeliverySlot}.`
                  : 'Your delivery time has been updated. Please check the latest slot below.')
                : order.customerUpdate?.message;
              const customerFacingOrderStatus =
                order.orderStatus === 'Delayed'
                  ? 'Delivery updated'
                  : order.orderStatus === 'Partially Delivered'
                    ? 'Partially delivered'
                    : order.orderStatus;
              const isDelivered =
                order.orderStatus?.toLowerCase() === 'delivered' ||
                order.deliveryStatus?.toLowerCase() === 'delivered';
              const hasActiveDeliveryOtp = Boolean(order.deliveryOtp) && !isDelivered;

              return (
                <ThemedCard
                  key={order._id}
                  className='overflow-hidden hover:shadow-lg transition-all duration-200'
                  theme={theme}
                >
                  {/* Order Header Section - RESPONSIVE */}
                  <div
                    className='p-3 sm:p-4 md:p-6 border-b'
                    style={{
                      backgroundColor: theme?.primaryColor ? `${theme.primaryColor}${isDark ? '15' : '05'}` : (isDark ? '#1e293b' : '#f8fafc'),
                      borderColor: isDark ? '#374151' : '#f1f5f9',
                    }}
                  >
                    <div className='flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 sm:gap-4'>
                      <div className='flex items-start space-x-3 sm:space-x-4 flex-1'>
                        <div
                          className='p-2 sm:p-3 rounded-lg flex-shrink-0'
                          style={{
                            backgroundColor: theme?.primaryColor || '#4f46e5',
                            borderRadius: theme?.borderRadius || '8px',
                          }}
                        >
                          <MdShoppingBag className='text-white text-lg sm:text-xl' />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <div className='flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2'>
                            <h3
                              className='font-semibold text-sm sm:text-lg'
                              style={{
                                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              {t("STORENEWDETAILS.ORDER_HISTORY.ORDER")} #{order.orderId}
                            </h3>
                            <span
                              className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-sm font-medium border ${getStatusColor(
                                order.orderStatus
                              )}`}
                            >
                              {customerFacingOrderStatus || t("STORENEWDETAILS.STATUS.PENDING")}
                            </span>
                            {hasTracking && (
                              <span
                                className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-sm font-medium border ${getTrackingStatusColor(
                                  tracking.status
                                )} flex items-center gap-1 sm:gap-2`}
                              >
                                {getTrackingStatusIcon(tracking.status)}
                                {tracking.status?.replace(/_/g, " ") || t("STORENEWDETAILS.STATUS.UNKNOWN")}
                              </span>
                            )}
                          </div>

                          {hasActiveDeliveryOtp && (
                            <div
                              className='inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 mt-1.5 sm:mt-2 rounded-lg border text-xs sm:text-sm'
                              style={{
                                backgroundColor: theme?.secondaryColor ? `${theme.secondaryColor}10` : '#ecfdf5',
                                borderColor: theme?.secondaryColor ? `${theme.secondaryColor}30` : '#a7f3d0',
                                color: theme?.secondaryColor || '#065f46',
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              <span className='font-medium'>Delivery code:</span>
                              <span className='font-bold tracking-widest text-sm sm:text-base'>{order.deliveryOtp}</span>
                            </div>
                          )}

                          <p
                            className='text-xs sm:text-sm flex items-center gap-1 mb-0.5 sm:mb-1'
                            style={{
                              color: theme?.textColor + '80' || '#6b7280',
                              fontFamily: theme?.fontFamily || 'Inter',
                            }}
                          >
                            <span>📅</span>
                            {t("STORENEWDETAILS.ORDER_HISTORY.ORDERED_ON")} {formatDate(order.orderDate || order.createdAt)}
                          </p>
                          <p
                            className='text-xs sm:text-sm flex items-start gap-1'
                            style={{
                              color: theme?.textColor || '#1f2937',
                              fontFamily: theme?.fontFamily || 'Inter',
                            }}
                          >
                            <MdLocationOn
                              className='mt-0.5 flex-shrink-0 text-sm sm:text-base'
                              style={{
                                color: theme?.primaryColor || '#4f46e5',
                              }}
                            />
                            <span className="line-clamp-1">{order.address || t("STORENEWDETAILS.ORDER_HISTORY.ADDRESS")}</span>
                          </p>

                          {order.customerUpdate?.message && (
                            <div
                              className='mt-2 sm:mt-3 rounded-lg sm:rounded-xl border p-2.5 sm:p-3'
                              style={{
                                backgroundColor:
                                  isDeliveryUpdated
                                    ? '#fff7ed'
                                    : '#eff6ff',
                                borderColor:
                                  isDeliveryUpdated
                                    ? '#fdba74'
                                    : '#93c5fd',
                              }}
                            >
                              <div className='flex items-start justify-between gap-2 sm:gap-3'>
                                <div>
                                  <p
                                    className='text-xs sm:text-sm font-semibold'
                                    style={{
                                      color:
                                        isDeliveryUpdated
                                          ? '#c2410c'
                                          : '#1d4ed8',
                                      fontFamily: theme?.fontFamily || 'Inter',
                                    }}
                                  >
                                    {customerUpdateTitle}
                                  </p>
                                  <p
                                    className='mt-0.5 sm:mt-1 text-[10px] sm:text-sm'
                                    style={{
                                      color:
                                        isDeliveryUpdated
                                          ? '#9a3412'
                                          : '#1e40af',
                                      fontFamily: theme?.fontFamily || 'Inter',
                                    }}
                                  >
                                    {customerUpdateMessage}
                                  </p>
                                  {revisedDeliverySlot && (
                                    <p
                                      className='mt-1.5 sm:mt-2 text-xs sm:text-sm font-medium'
                                      style={{
                                        color:
                                          isDeliveryUpdated
                                            ? '#c2410c'
                                            : '#1d4ed8',
                                        fontFamily: theme?.fontFamily || 'Inter',
                                      }}
                                    >
                                      Updated delivery slot:{' '}
                                      {revisedDeliverySlot}
                                    </p>
                                  )}
                                </div>
                                {order.customerUpdate?.updatedAt && (
                                  <span
                                    className='text-[10px] sm:text-xs whitespace-nowrap'
                                    style={{
                                      color:
                                        isDeliveryUpdated
                                          ? '#c2410c'
                                          : '#2563eb',
                                      fontFamily: theme?.fontFamily || 'Inter',
                                    }}
                                  >
                                    {formatDateTime(order.customerUpdate.updatedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Customer Info */}
                          <div
                            className='mt-1.5 sm:mt-2 flex flex-wrap gap-2 sm:gap-4 text-[10px] sm:text-xs'
                            style={{
                              color: theme?.textColor + '80' || '#6b7280',
                              fontFamily: theme?.fontFamily || 'Inter',
                            }}
                          >
                            <span>{t("STORENEWDETAILS.ORDER_HISTORY.CUSTOMER")}: {order.customerName}</span>
                            {order.phone && <span>{t("STORENEWDETAILS.ORDER_HISTORY.PHONE")}: {order.phone}</span>}
                          </div>

                          {/* AWB & Tracking Numbers */}
                          {(order.awbNumber || order.trackingNumber || order.shiprocket?.awbCode) && (
                            <div className='mt-2 sm:mt-3 flex flex-wrap gap-2 sm:gap-3'>
                              {(order.shiprocket?.awbCode || order.awbNumber) && (
                                <div className='inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border'
                                  style={{
                                    backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#dbeafe',
                                    borderColor: theme?.primaryColor ? `${theme.primaryColor}30` : '#bfdbfe',
                                    borderRadius: theme?.borderRadius || '8px',
                                  }}
                                >
                                  <MdLocalShipping
                                    style={{ color: theme?.primaryColor || '#4f46e5' }}
                                    size={14}
                                    className="sm:text-base"
                                  />
                                  <span className='text-[10px] sm:text-xs' style={{ color: theme?.primaryColor || '#4f46e5' }}>
                                    {t("STORENEWDETAILS.ORDER_HISTORY.AWB")}:
                                  </span>
                                  <span className='font-mono text-xs sm:text-sm font-semibold truncate max-w-[100px] sm:max-w-none' style={{ color: theme?.primaryColor || '#4f46e5' }}>
                                    {order.shiprocket?.awbCode || order.awbNumber}
                                  </span>
                                </div>
                              )}
                              {order.trackingNumber && (
                                <div className='inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border'
                                  style={{
                                    backgroundColor: theme?.secondaryColor ? `${theme.secondaryColor}10` : '#d1fae5',
                                    borderColor: theme?.secondaryColor ? `${theme.secondaryColor}30` : '#a7f3d0',
                                    borderRadius: theme?.borderRadius || '8px',
                                  }}
                                >
                                  <MdReceipt
                                    style={{ color: theme?.secondaryColor || '#10b981' }}
                                    size={14}
                                    className="sm:text-base"
                                  />
                                  <span className='text-[10px] sm:text-xs' style={{ color: theme?.secondaryColor || '#10b981' }}>
                                    {t("STORENEWDETAILS.ORDER_HISTORY.TRACKING")}:
                                  </span>
                                  <span className='font-mono text-xs sm:text-sm font-semibold truncate max-w-[100px] sm:max-w-none' style={{ color: theme?.secondaryColor || '#10b981' }}>
                                    {order.trackingNumber}
                                  </span>
                                </div>
                              )}
                              {order.shiprocket?.courierName && (
                                <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                                  style={{ borderRadius: theme?.borderRadius || '8px' }}
                                >
                                  <MdStore className='text-sm sm:text-base' size={14} style={{ color: theme?.textColor || (isDark ? '#9ca3af' : '#4b5563') }} />
                                  <span className='text-[10px] sm:text-xs' style={{ color: theme?.textColor || (isDark ? '#9ca3af' : '#4b5563') }}>{t("STORENEWDETAILS.ORDER_HISTORY.COURIER")}:</span>
                                  <span className='text-xs sm:text-sm font-medium truncate max-w-[80px] sm:max-w-none' style={{ color: theme?.headingColor || (isDark ? '#e5e7eb' : '#1f2937') }}>{order.shiprocket.courierName}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='text-left lg:text-right mt-2 lg:mt-0 flex lg:block items-center justify-between lg:justify-start gap-2'>
                        <p
                          className='font-bold text-xl sm:text-2xl mb-0 lg:mb-2'
                          style={{
                            color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          ₹{order.total?.toFixed(2) || "0.00"}
                        </p>
                        <div
                          className='flex items-center lg:justify-end gap-1 text-xs sm:text-sm'
                          style={{
                            color: theme?.textColor + '80' || '#6b7280',
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {order.deliveryMethod === "home_delivery" ? (
                            <MdLocalShipping
                              style={{
                                color: theme?.secondaryColor || '#10b981',
                              }}
                              className="text-sm sm:text-base"
                            />
                          ) : (
                            <MdStore
                              style={{
                                color: theme?.primaryColor || '#4f46e5',
                              }}
                              className="text-sm sm:text-base"
                            />
                          )}
                          <span className='capitalize text-[10px] sm:text-sm'>
                            {order.deliveryMethod === "home_delivery"
                              ? t("STORENEWDETAILS.ORDER_HISTORY.HOME_DELIVERY")
                              : order.deliveryMethod === "store_pickup"
                                ? t("STORENEWDETAILS.ORDER_HISTORY.STORE_PICKUP")
                                : t("STORENEWDETAILS.ORDER_HISTORY.STANDARD_DELIVERY")}
                          </span>
                        </div>
                        <div className='mt-0 lg:mt-1'>
                          <span
                            className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${order.paymentStatus === "paid"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                              }`}
                          >
                            {order.paymentStatus === "paid"
                              ? t("STORENEWDETAILS.ORDER_HISTORY.PAID")
                              : t("STORENEWDETAILS.ORDER_HISTORY.PENDING")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* TRACKING SECTION - RESPONSIVE */}
                    {hasTracking && (
                      <div
                        className='mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg border'
                        style={{
                          backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#dbeafe',
                          borderColor: theme?.primaryColor ? `${theme.primaryColor}30` : '#bfdbfe',
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4'>
                          <div className='flex items-center gap-2 sm:gap-3'>
                            <div
                              className='bg-white p-1.5 sm:p-2 rounded-full'
                              style={{
                                borderRadius: theme?.borderRadius || '9999px',
                              }}
                            >
                              <MdHistory
                                className='text-lg sm:text-xl'
                                style={{
                                  color: theme?.primaryColor || '#4f46e5',
                                }}
                              />
                            </div>
                            <div>
                              <p
                                className='font-semibold text-xs sm:text-sm'
                                style={{
                                  color: theme?.primaryColor || '#4f46e5',
                                  fontFamily: theme?.fontFamily || 'Inter',
                                }}
                              >
                                {t("STORENEWDETAILS.ORDER_HISTORY.TRACKING_NUMBER")}:{" "}
                                <span className='font-mono text-xs sm:text-sm'>
                                  {tracking.tracking_number}
                                </span>
                                {isMockNumber && (
                                  <span
                                    className='ml-1.5 sm:ml-2 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded'
                                    style={{
                                      backgroundColor: "#fef3c7",
                                      color: "#92400e",
                                    }}
                                  >
                                    Demo
                                  </span>
                                )}
                              </p>
                              <p
                                className='text-[10px] sm:text-sm'
                                style={{
                                  color: theme?.primaryColor + '80' || '#4f46e5',
                                  fontFamily: theme?.fontFamily || 'Inter',
                                }}
                              >
                                {t("STORENEWDETAILS.ORDER_HISTORY.CARRIER")}: {tracking.carrier_name}
                              </p>
                              {tracking.estimated_delivery && (
                                <p
                                  className='text-[10px] sm:text-sm'
                                  style={{
                                    color: theme?.primaryColor + '80' || '#4f46e5',
                                    fontFamily: theme?.fontFamily || 'Inter',
                                  }}
                                >
                                  {t("STORENEWDETAILS.ORDER_HISTORY.ESTIMATED_DELIVERY")}: {formatDate(tracking.estimated_delivery)}
                                </p>
                              )}
                              <p
                                className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${tracking.source === "real_api"
                                    ? "bg-green-100 text-green-800"
                                    : tracking.source === "test_api"
                                      ? "bg-blue-100 text-blue-800"
                                      : tracking.source === "enhanced_mock"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : tracking.source === "local_mock"
                                          ? "bg-orange-100 text-orange-800"
                                          : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {tracking.source === "real_api" && t("STORENEWDETAILS.TRACKING_SOURCES.REAL_API")}
                                {tracking.source === "test_api" && t("STORENEWDETAILS.TRACKING_SOURCES.TEST_API")}
                                {tracking.source === "enhanced_mock" && t("STORENEWDETAILS.TRACKING_SOURCES.ENHANCED_MOCK")}
                                {tracking.source === "local_mock" && t("STORENEWDETAILS.TRACKING_SOURCES.LOCAL_MOCK")}
                                {!tracking.source && t("STORENEWDETAILS.TRACKING_SOURCES.DEFAULT")}
                              </p>
                            </div>
                          </div>
                          <div className='flex flex-wrap gap-2'>
                            <button
                              onClick={() => refreshTracking(order.orderId)}
                              className='flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm'
                              style={{
                                backgroundColor: theme?.buttonColor || '#4f46e5',
                                color: '#ffffff',
                                borderRadius: theme?.buttonStyle === 'pill' ? '9999px' :
                                  theme?.buttonStyle === 'rounded' ? '8px' : '4px',
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              <MdSchedule size={14} className="sm:text-base" />
                              {t("STORENEWDETAILS.ORDER_HISTORY.REFRESH_STATUS")}
                            </button>
                            {!isMockNumber && tracking.tracking_number && (
                              <a
                                href={
                                  tracking.tracking_url ||
                                  `https://www.${tracking.carrier}.com/track/${tracking.tracking_number}`
                                }
                                target='_blank'
                                rel='noopener noreferrer'
                                className='flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-white rounded-lg font-medium transition-colors text-xs sm:text-sm'
                                style={{
                                  backgroundColor: theme?.secondaryColor || '#10b981',
                                  borderRadius: theme?.borderRadius || '8px',
                                  fontFamily: theme?.fontFamily || 'Inter',
                                }}
                              >
                                <MdStore size={14} className="sm:text-base" />
                                {t("STORENEWDETAILS.ORDER_HISTORY.TRACK_ON_CARRIER")}
                              </a>
                            )}
                            <button
                              onClick={() => toggleExpand(order._id)}
                              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border rounded-lg font-medium transition-colors text-xs sm:text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                              style={{
                                borderColor: '#e5e7eb',
                                borderRadius: theme?.borderRadius || '8px',
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              {isExpanded ? (
                                <>
                                  <MdExpandLess size={14} className="sm:text-base" />
                                  {t("STORENEWDETAILS.ORDER_HISTORY.HIDE_DETAILS")}
                                </>
                              ) : (
                                <>
                                  <MdExpandMore size={14} className="sm:text-base" />
                                  {t("STORENEWDETAILS.ORDER_HISTORY.SHOW_DETAILS")}
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Tracking History */}
                        {isExpanded && tracking.events && (
                          <div className='mt-3 sm:mt-4 space-y-2 sm:space-y-3'>
                            <h4
                              className='font-semibold text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2'
                              style={{
                                color: theme?.primaryColor || '#4f46e5',
                                fontFamily: theme?.fontFamily || 'Inter',
                              }}
                            >
                              <MdLocalShipping className="text-sm sm:text-base" />
                              {t("STORENEWDETAILS.ORDER_HISTORY.TRACKING_HISTORY")}
                            </h4>
                            <div className='space-y-1.5 sm:space-y-2'>
                              {tracking.events.map((event, index) => (
                                <ThemedCard
                                  key={index}
                                  className='flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3'
                                  theme={theme}
                                  hasBorder={true}
                                >
                                  <div
                                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${event.status === "delivered"
                                        ? "bg-green-100 text-green-600"
                                        : event.status === "in_transit"
                                          ? "bg-blue-100 text-blue-600"
                                          : event.status === "out_for_delivery"
                                            ? "bg-orange-100 text-orange-600"
                                            : "bg-yellow-100 text-yellow-600"
                                      }`}
                                    style={{
                                      borderRadius: theme?.borderRadius || '9999px',
                                    }}
                                  >
                                    {getTrackingStatusIcon(event.status)}
                                  </div>
                                  <div className='flex-1 min-w-0'>
                                    <div className='flex justify-between items-start gap-2'>
                                      <p
                                        className='font-medium capitalize text-xs sm:text-sm'
                                        style={{
                                          color: theme?.textColor || '#1f2937',
                                          fontFamily: theme?.fontFamily || 'Inter',
                                        }}
                                      >
                                        {event.status?.replace(/_/g, " ") || t("STORENEWDETAILS.STATUS.UNKNOWN")}
                                      </p>
                                      <span
                                        className='text-[10px] sm:text-xs whitespace-nowrap'
                                        style={{
                                          color: theme?.textColor + '80' || '#6b7280',
                                          fontFamily: theme?.fontFamily || 'Inter',
                                        }}
                                      >
                                        {formatDateTime(event.timestamp)}
                                      </span>
                                    </div>
                                    <p
                                      className='text-[10px] sm:text-sm mt-0.5 sm:mt-1'
                                      style={{
                                        color: theme?.textColor + '80' || '#6b7280',
                                        fontFamily: theme?.fontFamily || 'Inter',
                                      }}
                                    >
                                      {event.description}
                                    </p>
                                    {event.location && (
                                      <p
                                        className='text-[10px] sm:text-xs mt-0.5 sm:mt-1 flex items-center gap-1'
                                        style={{
                                          color: theme?.textColor + '80' || '#6b7280',
                                          fontFamily: theme?.fontFamily || 'Inter',
                                        }}
                                      >
                                        <MdLocationOn size={10} className="sm:text-xs" />
                                        {event.location}
                                      </p>
                                    )}
                                  </div>
                                </ThemedCard>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Tracking Message */}
                    {!hasTracking && order.orderStatus === "Shipped" && (
                      <div
                        className='mt-3 sm:mt-4 p-2.5 sm:p-3 rounded-lg border'
                        style={{
                          backgroundColor: "#fef3c7",
                          borderColor: "#f59e0b",
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        <div className='flex items-center gap-1.5 sm:gap-2'>
                          <MdLocalShipping style={{ color: "#f59e0b" }} className="text-sm sm:text-base" />
                          <p className='text-xs sm:text-sm' style={{ color: "#92400e", fontFamily: theme?.fontFamily || 'Inter' }}>
                            {t("STORENEWDETAILS.ORDER_HISTORY.SHIPPED_MESSAGE")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='p-3 sm:p-4 md:p-6'>
                    <div className='grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4 mb-4 sm:mb-6'>
                      <div
                        className='p-1.5 sm:p-2 md:p-3 rounded-lg'
                        style={{
                          backgroundColor: theme?.backgroundColor ? adjustColor(theme.backgroundColor, 5) : '#f9fafb',
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        <p
                          className='text-[9px] sm:text-xs md:text-sm font-medium truncate'
                          style={{
                            color: theme?.textColor + '80' || '#6b7280',
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {t("STORENEWDETAILS.ORDER_HISTORY.ITEMS")}
                        </p>
                        <p
                          className='text-xs sm:text-sm md:text-lg font-semibold'
                          style={{
                            color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {order.items?.length || 0}
                        </p>
                      </div>
                      <div
                        className='p-1.5 sm:p-2 md:p-3 rounded-lg'
                        style={{
                          backgroundColor: theme?.backgroundColor ? adjustColor(theme.backgroundColor, 5) : '#f9fafb',
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        <p
                          className='text-[9px] sm:text-xs md:text-sm font-medium truncate'
                          style={{
                            color: theme?.textColor + '80' || '#6b7280',
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {t("STORENEWDETAILS.ORDER_HISTORY.PAYMENT_METHOD")}
                        </p>
                        <p
                          className='text-xs sm:text-sm md:text-lg font-semibold capitalize truncate'
                          style={{
                            color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {order.paymentMethod?.replace(/_/g, " ") || t("STORENEWDETAILS.ORDER_HISTORY.NOT_SPECIFIED")}
                        </p>
                      </div>
                      <div
                        className='p-1.5 sm:p-2 md:p-3 rounded-lg'
                        style={{
                          backgroundColor: theme?.backgroundColor ? adjustColor(theme.backgroundColor, 5) : '#f9fafb',
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        <p
                          className='text-[9px] sm:text-xs md:text-sm font-medium truncate'
                          style={{
                            color: theme?.textColor + '80' || '#6b7280',
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {t("STORENEWDETAILS.ORDER_HISTORY.LAST_UPDATED")}
                        </p>
                        <p
                          className='text-[10px] sm:text-sm md:text-lg font-semibold truncate'
                          style={{
                            color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          {formatDate(order.updatedAt)}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons - RESPONSIVE */}
                    <div className='flex flex-wrap gap-2'>
                      <button
                        className='flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 border text-xs sm:text-sm'
                        onClick={() => toggleExpand(order._id)}
                        style={{
                          backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#dbeafe',
                          color: theme?.primaryColor || '#4f46e5',
                          borderColor: theme?.primaryColor ? `${theme.primaryColor}30` : '#bfdbfe',
                          borderRadius: theme?.borderRadius || '8px',
                          fontFamily: theme?.fontFamily || 'Inter',
                        }}
                      >
                        {isExpanded ? (
                          <>
                            <MdExpandLess className='text-sm' />
                            {t("STORENEWDETAILS.ORDER_HISTORY.HIDE_DETAILS")}
                          </>
                        ) : (
                          <>
                            <MdExpandMore className='text-sm' />
                            {t("STORENEWDETAILS.ORDER_HISTORY.DETAILS")}
                          </>
                        )}
                      </button>
                      <button
                        className='flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 border text-xs sm:text-sm'
                        onClick={() =>
                          navigate(
                            getStorefrontPath({
                              businessName: displayBusinessName,
                              bussinessUuid,
                              suffix: `invoice/${order.orderId}`,
                            }),
                          )
                        }
                        style={{
                          backgroundColor: theme?.secondaryColor ? `${theme.secondaryColor}10` : '#d1fae5',
                          color: theme?.secondaryColor || '#10b981',
                          borderColor: theme?.secondaryColor ? `${theme.secondaryColor}30` : '#a7f3d0',
                          borderRadius: theme?.borderRadius || '8px',
                          fontFamily: theme?.fontFamily || 'Inter',
                        }}
                      >
                        <MdReceipt className='text-sm sm:text-lg' />
                        {t("STORENEWDETAILS.ORDER_HISTORY.VIEW_INVOICE")}
                      </button>

                      <CancelOrderButton
                        order={order}
                        bussinessUuid={bussinessUuid}
                        theme={theme}
                        onSuccess={fetchOrders}
                        setPageLoading={setPageLoading}
                      />

                      <ReturnOrderButton
                        order={order}
                        bussinessUuid={bussinessUuid}
                        theme={theme}
                        onSuccess={fetchOrders}
                        setPageLoading={setPageLoading}
                      />
                    </div>

                    {isExpanded && (
                      <div
                        className='border p-3 sm:p-4 mt-3 sm:mt-4 animate-fadeIn'
                        style={{
                          backgroundColor: isDark ? adjustColor(theme?.backgroundColor || '#1f2937', 10) : '#ffffff',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          borderRadius: theme?.borderRadius || '8px',
                        }}
                      >
                        {getDeliveryInvoices(order).length > 0 && (
                          <div className='mb-3 sm:mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 sm:p-3'>
                            <h5 className='mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-emerald-800'>
                              <MdReceipt className='text-sm sm:text-base' />
                              Delivery Invoice Downloads ({getDeliveryInvoices(order).length})
                            </h5>
                            <div className='space-y-2 sm:space-y-3'>
                              {getDeliveryInvoices(order).map((invoice) => (
                                <div
                                  key={invoice.saleId || invoice.invoiceId || invoice.batchId || invoice.sequenceLabel}
                                  className='rounded-md border border-emerald-200 bg-white p-2.5 sm:p-3'
                                >
                                  <div className='flex flex-wrap items-center justify-between gap-2'>
                                    <div className='flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-emerald-900'>
                                      <span className='rounded-full bg-emerald-100 px-1.5 sm:px-2 py-0.5 font-semibold'>
                                        {invoice.sequenceLabel}
                                      </span>
                                      <span className='font-semibold'>Invoice #{invoice.invoiceId || "N/A"}</span>
                                      <span className='rounded-full bg-emerald-100 px-1.5 sm:px-2 py-0.5 text-emerald-800'>
                                        Total: ₹{Number(invoice?.amount || 0).toFixed(2)}
                                      </span>
                                      <span className='rounded-full bg-blue-50 px-1.5 sm:px-2 py-0.5 text-blue-700'>
                                        Created: {formatInvoiceDateTime(invoice.createdAt)}
                                      </span>
                                    </div>

                                    {(Number(invoice?.shippingAmount || 0) > 0 || Number(invoice?.taxAmount || 0) > 0) && (
                                      <div className='w-full text-[10px] sm:text-xs text-gray-600'>
                                        Subtotal: ₹{Number(invoice?.subtotalAmount || 0).toFixed(2)} | Tax: ₹{Number(invoice?.taxAmount || 0).toFixed(2)} | Shipping: ₹{Number(invoice?.shippingAmount || 0).toFixed(2)}
                                      </div>
                                    )}

                                    <div className='flex items-center gap-2'>
                                      <button
                                        type='button'
                                        onClick={() =>
                                          handleDownloadInvoicePdf({
                                            order,
                                            invoice,
                                            businessName: displayBusinessName,
                                            bussinessUuid,
                                          })
                                        }
                                        className='inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] sm:text-xs font-medium text-emerald-700 hover:bg-emerald-100'
                                      >
                                        <MdDownload className='text-xs sm:text-sm' /> Download PDF
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <h4
                          className='font-semibold text-sm sm:text-lg mb-3 sm:mb-4 flex items-center gap-1.5 sm:gap-2'
                          style={{
                            color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                            fontFamily: theme?.fontFamily || 'Inter',
                          }}
                        >
                          <MdShoppingBag style={{ color: theme?.primaryColor || '#4f46e5' }} className="text-sm sm:text-base" />
                          {t("STORENEWDETAILS.ORDER_HISTORY.ORDER_ITEMS")} ({order.items?.length || 0})
                          {isDelivered && (
                            <span className="text-xs sm:text-sm font-normal text-green-600 ml-1.5 sm:ml-2">
                              ✓ {t("STORENEWDETAILS.ORDER_HISTORY.DELIVERED")}
                            </span>
                          )}
                        </h4>
                        <div className='space-y-2 sm:space-y-3 max-h-60 sm:max-h-80 overflow-y-auto'>
                          {order.items?.map((item, idx) => {
                            const Price = isB2BMode
                              ? Number(item.b2bSalePrice || item.salePrice || 0)
                              : Number(item.salePrice || 0);
                            const deliverySnapshot = getItemDeliverySnapshot(order, item, idx);

                            const itemTotal = Price * (item.cartQuantity || 1);
                            const discountAmount =
                              item.saleDiscountUnit === "₹"
                                ? item.saleDiscount || 0
                                : (itemTotal * (item.saleDiscount || 0)) / 100;
                            const finalPrice = itemTotal - discountAmount;
                            const productId = item.productId || item._id;
                            const existingReview = productReviews[productId];
                            const productReviewed = isProductReviewed(item);

                            const formatVariantAttributes = (item) => {
                              const variantData = item.selectedVariant || item.variant || {};
                              const attributes = variantData.attributes || item.variantAttributes || [];

                              if (attributes && attributes.length > 0) {
                                return attributes.map(attr =>
                                  `${attr.type || attr.attributeType}: ${attr.value}`
                                ).join(' • ');
                              }

                              if (variantData.variantName || item.variantName) {
                                return variantData.variantName || item.variantName;
                              }

                              if (variantData.variantKey || item.variantKey) {
                                const variantKey = variantData.variantKey || item.variantKey;
                                if (variantKey && variantKey !== 'default') {
                                  return variantKey.split('|').map(part => {
                                    const [type, value] = part.split(':');
                                    return `${type}: ${value}`;
                                  }).join(' • ');
                                }
                              }

                              return null;
                            };

                            const variantDisplay = formatVariantAttributes(item);

                            return (
                              <ThemedCard
                                key={idx}
                                className='flex flex-col sm:flex-row items-start sm:items-center justify-between p-2.5 sm:p-3 hover:border-blue-200 transition-all duration-200 gap-2 sm:gap-0'
                                theme={theme}
                              >
                                <div className='flex items-center space-x-2.5 sm:space-x-3 flex-1 min-w-0 w-full sm:w-auto'>
                                  <div
                                    className='w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0'
                                    style={{
                                      backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#dbeafe',
                                      borderRadius: theme?.borderRadius || '8px',
                                    }}
                                  >
                                    {item.itemPicture ? (
                                      <img
                                        src={item.itemPicture.includes('http') ? item.itemPicture : `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${item.itemPicture}`}
                                        alt={item.productName}
                                        className='w-full h-full object-cover'
                                      />
                                    ) : (
                                      <MdShoppingBag
                                        style={{
                                          color: theme?.primaryColor || '#4f46e5',
                                        }}
                                        size={20}
                                        className="sm:text-2xl"
                                      />
                                    )}
                                  </div>
                                  <div className='flex-1 min-w-0'>
                                    <p
                                      className='font-medium text-xs sm:text-sm truncate'
                                      style={{
                                        color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                                        fontFamily: theme?.fontFamily || 'Inter',
                                      }}
                                    >
                                      {item.productName || `${t("STORENEWDETAILS.ORDER_HISTORY.ITEM")} ${idx + 1}`}
                                    </p>

                                    {variantDisplay && (
                                      <div className="mt-0.5 sm:mt-1">
                                        <span
                                          className="inline-block text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#f3f4f6',
                                            color: theme?.primaryColor || '#4f46e5',
                                            border: `1px solid ${theme?.primaryColor ? `${theme.primaryColor}30` : '#e5e7eb'}`,
                                            borderRadius: theme?.borderRadius || '4px',
                                          }}
                                        >
                                          {variantDisplay}
                                        </span>
                                      </div>
                                    )}

                                    <div
                                      className='flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-sm mt-0.5 sm:mt-1'
                                      style={{
                                        color: theme?.textColor + '80' || '#6b7280',
                                        fontFamily: theme?.fontFamily || 'Inter',
                                      }}
                                    >
                                      <span>{t("STORENEWDETAILS.ORDER_HISTORY.QUANTITY")}: {item.cartQuantity || 1}</span>
                                      <span className='rounded-full bg-emerald-50 px-1.5 sm:px-2 py-0.5 text-emerald-700'>
                                        Received: {deliverySnapshot.deliveredQuantity}
                                      </span>
                                      <span className='rounded-full bg-amber-50 px-1.5 sm:px-2 py-0.5 text-amber-700'>
                                        Pending: {deliverySnapshot.pendingQuantity}
                                      </span>
                                      {deliverySnapshot.lastDeliveredAt && (
                                        <span className='rounded-full bg-blue-50 px-1.5 sm:px-2 py-0.5 text-blue-700'>
                                          Delivered on: {formatDateTime(deliverySnapshot.lastDeliveredAt)}
                                        </span>
                                      )}
                                      <span>₹{isB2BMode ? item.b2bSalePrice : item.salePrice} {t("STORENEWDETAILS.ORDER_HISTORY.EACH")}</span>
                                      {discountAmount > 0 && (
                                        <span
                                          className='font-medium'
                                          style={{
                                            color: theme?.secondaryColor || '#10b981',
                                            fontFamily: theme?.fontFamily || 'Inter',
                                          }}
                                        >
                                          {t("STORENEWDETAILS.ORDER_HISTORY.SAVE")} ₹{discountAmount.toFixed(2)}
                                        </span>
                                      )}
                                    </div>

                                    {item.sku_number && item.sku_number !== "undefined" && (
                                      <p
                                        className='text-[10px] sm:text-xs mt-0.5 sm:mt-1'
                                        style={{
                                          color: theme?.textColor + '80' || '#6b7280',
                                          fontFamily: theme?.fontFamily || 'Inter',
                                        }}
                                      >
                                        {t("STORENEWDETAILS.ORDER_HISTORY.SKU")}: {item.sku_number}
                                      </p>
                                    )}

                                    {item.variantSku && item.variantSku !== "undefined" && (
                                      <p
                                        className='text-[10px] sm:text-xs mt-0.5'
                                        style={{
                                          color: theme?.textColor + '60' || '#9ca3af',
                                          fontFamily: theme?.fontFamily || 'Inter',
                                        }}
                                      >
                                        {t("STORENEWDETAILS.ORDER_HISTORY.VARIANT_SKU")}: {item.variantSku}
                                      </p>
                                    )}

                                    {isDelivered && (
                                      <div className='mt-1.5 sm:mt-2'>
                                        {productReviewed ? (
                                          <div className='flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm'>
                                            <span className='text-green-600 font-medium flex items-center gap-1'>
                                              <FaCheckCircle size={10} className="sm:text-xs" />
                                              {t("STORENEWDETAILS.ORDER_HISTORY.ALREADY_REVIEWED")}
                                            </span>
                                            {existingReview && renderStars(existingReview.rating, 'text-[10px] sm:text-sm')}
                                            {existingReview && existingReview.comment && existingReview.comment !== "Your review is being processed..." && (
                                              <button
                                                onClick={() => {
                                                  toast.info(existingReview.comment, {
                                                    position: "bottom-center",
                                                    autoClose: 5000,
                                                  });
                                                }}
                                                className="text-[10px] sm:text-xs text-blue-600 hover:underline ml-1.5 sm:ml-2"
                                              >
                                                {t("STORENEWDETAILS.ORDER_HISTORY.VIEW_REVIEW")}
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => handleOpenReviewModal(item, order)}
                                            className='flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium hover:opacity-90 transition-opacity'
                                            style={{
                                              color: theme?.primaryColor || '#4f46e5'
                                            }}
                                          >
                                            <MdRateReview size={14} className="sm:text-base" />
                                            {t("STORENEWDETAILS.ORDER_HISTORY.RATE_REVIEW")}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className='text-left sm:text-right w-full sm:w-auto flex sm:block items-center justify-between sm:justify-start mt-1 sm:mt-0 pl-12 sm:pl-0'>
                                  <p
                                    className='font-semibold text-sm sm:text-lg'
                                    style={{
                                      color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                                      fontFamily: theme?.fontFamily || 'Inter',
                                    }}
                                  >
                                    ₹{finalPrice.toFixed(2)}
                                  </p>
                                  {discountAmount > 0 && (
                                    <p
                                      className='text-xs sm:text-sm font-medium'
                                      style={{
                                        color: theme?.secondaryColor || '#10b981',
                                        fontFamily: theme?.fontFamily || 'Inter',
                                      }}
                                    >
                                      {t("STORENEWDETAILS.ORDER_HISTORY.SAVED")} ₹{discountAmount.toFixed(2)}
                                    </p>
                                  )}
                                  {!isDelivered && (
                                    <p className='text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1'>
                                      {t("STORENEWDETAILS.ORDER_HISTORY.REVIEW_AFTER_DELIVERY")}
                                    </p>
                                  )}
                                </div>
                              </ThemedCard>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </ThemedCard>
              );
            })}
          </div>
        ) : (
          <ThemedCard className='p-6 sm:p-8 text-center max-w-md mx-auto' theme={theme}>
            <div
              className='w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4'
              style={{
                backgroundColor: theme?.primaryColor ? `${theme.primaryColor}10` : '#dbeafe',
                borderRadius: theme?.borderRadius || '9999px',
              }}
            >
              <MdHistory
                className='text-xl sm:text-2xl'
                style={{ color: theme?.primaryColor + '80' || '#4f46e5' }}
              />
            </div>
            <h3
              className='text-base sm:text-lg font-bold mb-1.5 sm:mb-2'
              style={{
                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.NO_ORDERS")}
            </h3>
            <p
              className='text-xs sm:text-sm mb-4 sm:mb-6'
              style={{
                color: theme?.textColor + '80' || '#6b7280',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.NO_ORDERS_MESSAGE")}
            </p>
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                  }),
                )
              }
              className='w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm shadow-md hover:shadow-lg transition-all duration-200'
              style={{
                backgroundColor: theme?.buttonColor || '#4f46e5',
                color: '#ffffff',
                borderRadius: theme?.buttonStyle === 'pill' ? '9999px' :
                  theme?.buttonStyle === 'rounded' ? '8px' : '4px',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {t("STORENEWDETAILS.ORDER_HISTORY.START_SHOPPING")}
            </button>
          </ThemedCard>
        )}
      </div>

      {bookingReviewModal.isOpen && bookingReviewModal.booking && (
        <div
          className='fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm'
          onClick={closeBookingReviewModal}
        >
          <div
            className='w-full max-w-lg rounded-2xl p-5 sm:p-6'
            onClick={(event) => event.stopPropagation()}
            style={{
              backgroundColor: isDark ? '#0f172a' : '#ffffff',
              border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
              borderRadius: theme?.borderRadius || '12px',
            }}
          >
            <h3
              className='text-lg sm:text-xl font-bold'
              style={{
                color: theme?.headingColor || (isDark ? '#f9fafb' : '#111827'),
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              How was your service experience?
            </h3>
            <p
              className='mt-1 text-sm'
              style={{
                color: theme?.textColor || (isDark ? '#cbd5e1' : '#475569'),
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            >
              {bookingReviewModal.booking?.serviceName || 'Service'} on {formatDate(bookingReviewModal.booking?.date)} at {bookingReviewModal.booking?.time}
            </p>

            <div className='mt-4 flex gap-2'>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type='button'
                  onClick={() => setBookingReviewForm((prev) => ({ ...prev, rating: star }))}
                  className={`text-3xl ${star <= bookingReviewForm.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                >
                  ★
                </button>
              ))}
            </div>

            <input
              type='text'
              value={bookingReviewForm.title}
              onChange={(event) =>
                setBookingReviewForm((prev) => ({ ...prev, title: event.target.value }))
              }
              placeholder='Title (optional)'
              className='mt-4 w-full rounded-lg px-3 py-2 text-sm'
              style={{
                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                color: theme?.textColor || (isDark ? '#f1f5f9' : '#0f172a'),
                border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
                borderRadius: theme?.borderRadius || '8px',
                fontFamily: theme?.fontFamily || 'Inter',
              }}
            />

            <textarea
              rows={4}
              value={bookingReviewForm.comment}
              onChange={(event) =>
                setBookingReviewForm((prev) => ({ ...prev, comment: event.target.value }))
              }
              placeholder='Write your review...'
              className='mt-3 w-full rounded-lg px-3 py-2 text-sm'
              style={{
                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                color: theme?.textColor || (isDark ? '#f1f5f9' : '#0f172a'),
                border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
                borderRadius: theme?.borderRadius || '8px',
                fontFamily: theme?.fontFamily || 'Inter',
                resize: 'vertical',
              }}
            />

            <div className='mt-4 flex items-center justify-end gap-2'>
              <button
                type='button'
                onClick={closeBookingReviewModal}
                className='px-3 py-1.5 rounded-lg text-sm font-medium'
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                  color: theme?.textColor || (isDark ? '#e2e8f0' : '#475569'),
                  border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
                  borderRadius: theme?.borderRadius || '8px',
                  fontFamily: theme?.fontFamily || 'Inter',
                }}
              >
                Later
              </button>
              <button
                type='button'
                onClick={handleSubmitBookingReview}
                disabled={bookingReviewSubmitting}
                className='px-4 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-70'
                style={{
                  backgroundColor: theme?.buttonColor || '#4f46e5',
                  borderRadius: theme?.borderRadius || '8px',
                  fontFamily: theme?.fontFamily || 'Inter',
                }}
              >
                {bookingReviewSubmitting ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReviewModal
        product={reviewModal.product}
        order={reviewModal.order}
        isOpen={reviewModal.isOpen}
        onClose={() => setReviewModal({ isOpen: false, product: null, order: null })}
        onReviewSubmitted={handleReviewSubmitted}
        theme={theme}
        businessUuid={bussinessUuid}
        customerProfile={customerProfile}
        key={reviewModal.product ? reviewModal.product.productId || reviewModal.product._id : 'no-product'}
      />
    </div>
  );
}

export default OrderHistoryPage;